// [S-095 — scaffold + S-095a/c/e/g wired] Settings — tenant admin console.
//
// Direct port of docs/Wireframes/Settings.html. The eight section
// sub-stories (S-095a–g, see docs/CSAI_DesignHandoff_2026-05-11.md §3.4)
// wire the backend per section.
//
// Status today:
//   General (S-095a + S-095g) — wired: reads live Tenant via
//                       GET /tenants/:id, including region + timeZone.
//                       Time zone edits go through
//                       PATCH /tenants/:id/time-zone with a per-section
//                       save (mirrors S-095c's pattern). Region is
//                       read-only on this surface: the wireframe hint
//                       says "Cannot be changed after first ingest.
//                       Open a ticket to migrate" — region migration
//                       is an out-of-band platform_admin operation.
//   Members (S-095e)  — wired: reads live users via GET /tenants/:id/users.
//                       Invite/role/remove CRUD lives at the dedicated
//                       /admin/tenants/:tenantId/users surface (shipped in
//                       S-091) — the section's "+ Invite member" button
//                       deep-links there rather than duplicating the
//                       modal stack in the Settings rail.
//   Security (S-095c) — wired: reads + writes the per-tenant policy via
//                       GET / PUT /tenants/:id/security. Save fires a
//                       single PUT (full replacement) and emits an
//                       audit event via TenantConfigService.
//   Connectors (S-095f), Look & feel (S-095b), RBAC (S-095d)
//                     — preview scaffold; "Preview · S-095x" badge on
//                       each section header. Bottom save bar disabled
//                       until at least one writable section ships.
//
// Role gate: <RequireRole minRole="tenant_admin"> in App.tsx.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import { effectiveCapabilities } from "../auth/capabilities";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  PageHead,
  SectionHead,
  Select,
  Spinner,
  Switch,
} from "../components";
import {
  useConnectors,
  useDeleteDriveConnector,
  useDriveConnectorStatus,
  useRegisterDriveFolder,
  useStartDriveOAuth,
  useSyncDriveConnector,
  type ConnectorRow,
} from "../data/connectorsQuery";
import {
  useSecurityPolicy,
  useUpdateSecurityPolicy,
  type AuditRetentionDays,
  type SecurityPolicyRow,
  type SsoProvider,
} from "../data/securityPolicyQuery";
import {
  useApiKeys,
  useIssueApiKey,
  useRevokeApiKey,
  type ApiKeyRecord,
} from "../data/apiKeysQuery";
import { useMcpAccess } from "../data/mcpAccessQuery";
import {
  useMcpDeployment,
  useDeployMcp,
  useUndeployMcp,
} from "../data/mcpDeployQuery";
import {
  BRANDING_COLORS,
  NOTIFICATION_EVENTS,
  useExportAuditLog,
  useTenantPreferences,
  useUpdateBranding,
  useUpdateNotifications,
  type BrandingPrimaryColor,
  type NotificationEvent,
} from "../data/tenantPreferencesQuery";
import { getApiErrorMessage } from "../data/api";
import {
  usePrecedentEngineConfig,
  useSavePrecedentEngineConfig,
  useTestPrecedentEngineConnection,
  type PrecedentEngineConnector,
  type PrecedentEngineMode,
  type PrecedentStorageProvider,
} from "../data/precedentEngineQuery";
import {
  useStartDocumentFormBackfill,
  useDocumentFormBackfillStatus,
  useCancelDocumentFormBackfill,
} from "../data/precedentsQuery";
import {
  useBackfillEstimate,
  useBackfillStatus,
  useDisablePrecedentAnalytics,
  useEnablePrecedentAnalytics,
  usePrecedentAnalyticsConfig,
  useStartBackfill,
  type PrecedentAnalyticsStatus,
} from "../data/precedentAnalyticsQuery";
import { useFeatureFlags, useSetFeatureFlag } from "../data/featureFlagsQuery";
import {
  useTenantDetail,
  useTenantsList,
  useTenantUsersList,
  useUpdateTenantTimeZone,
  type TenantRow,
  type TenantTimeZone,
  type TenantUserRow,
} from "../data/tenantsQuery";
import {
  useAssignCustomRole,
  useClearAssignedRole,
  useCustomRoles,
  useDefineCustomRole,
  useDeleteCustomRole,
} from "../data/rbacQuery";
import {
  CAPABILITIES,
  ROLE_CAPABILITIES,
  ROLES,
  type Capability,
  type Role,
} from "@csai/core/browser";

// [S-245 — look & feel] The Settings surface used to stack every section in
// one long scroll with an anchor-jump rail. Per the refreshed wireframe
// (docs/sample-context Settings.html) the rail is now a real tab list: only
// the active section renders, so the page is far easier to digest. Nothing
// about each section's wiring changed — the sections and their queries are
// identical; we just mount one panel at a time. Deep links still work: the
// active tab is seeded from (and kept in sync with) the URL hash, so the
// Drive OAuth callback redirect to `/settings#connectors` and the in-page
// `#apikeys` link from MCP access both land on the right tab.
const SECTION_IDS = [
  "general",
  "connectors",
  "precedent",
  // [S-617] Named "precedentAnalytics", not "analytics" — this app already
  // has an unrelated Analytics dashboard at /analytics (usage stats,
  // E-ANALYTICS). Don't conflate the two.
  "precedentAnalytics",
  "features",
  "look",
  "security",
  "rbac",
  "members",
  "apikeys",
  "mcp",
  "notifications",
  "data",
] as const;
type SectionId = (typeof SECTION_IDS)[number];

const SECTION_LABELS: Record<SectionId, string> = {
  general: "General",
  connectors: "Connectors",
  precedent: "Precedent engine",
  precedentAnalytics: "Precedent analytics",
  features: "Feature switches",
  look: "Look & feel",
  security: "Security",
  rbac: "Roles & permissions",
  members: "Members",
  apikeys: "API keys",
  mcp: "MCP access",
  notifications: "Notifications",
  data: "Data & retention",
};

function sectionFromHash(): SectionId {
  if (typeof window === "undefined") return "general";
  const raw = window.location.hash.replace(/^#/, "");
  return (SECTION_IDS as readonly string[]).includes(raw)
    ? (raw as SectionId)
    : "general";
}

export function Settings(): JSX.Element {
  const session = useSession();
  // Tab state, seeded from the URL hash so deep links (Drive OAuth callback →
  // #connectors, MCP access → #apikeys) open the right section. A hashchange
  // listener keeps the tab in sync when the hash is changed by something other
  // than a rail click (e.g. the in-page "API keys" link inside MCP access).
  const [active, setActive] = useState<SectionId>(sectionFromHash);
  useEffect(() => {
    const onHash = (): void => setActive(sectionFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const selectSection = useCallback((id: SectionId): void => {
    setActive(id);
    // replaceState (not location.hash =) so switching tabs doesn't push a
    // history entry or fire a scroll-into-view on the panel we're revealing.
    if (typeof window !== "undefined" && window.history?.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    }
    // The shell's <main class="main__body"> is the scroll container; reset it
    // so each tab opens at the top rather than inheriting the last scroll.
    const body = document.querySelector(".main__body");
    if (body instanceof HTMLElement) body.scrollTop = 0;
  }, []);
  // [S-315] Tenant ID convention: `tenant-${studioId}` (see deriveTenantId
  // in TenantService). platform_admin has studioId === null — the route
  // guard (RequireRole minRole=tenant_admin) lets them through, but every
  // tenant-scoped section needs a chosen organization to resolve against.
  // A tenant_admin's tenant is fixed from the session; a platform_admin
  // picks one via the picker below (server-side gates already authorize
  // platform_admin for any tenant — this is purely a UI gap).
  const [platformAdminTenantId, setPlatformAdminTenantId] = useState("");
  const isPlatformAdmin =
    session?.studioId === null || session?.studioId === undefined;
  const tenantsListQuery = useTenantsList({ enabled: isPlatformAdmin });
  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : platformAdminTenantId;
  const tenantQuery = useTenantDetail(tenantId);
  const usersQuery = useTenantUsersList(tenantId);
  const securityQuery = useSecurityPolicy(tenantId);
  // A platform_admin has no fixed studio — show the picked tenant's name
  // once chosen, else the generic fallback (matches prettyStudio(undefined)).
  const pickedTenant = tenantsListQuery.data?.tenants?.find(
    (t) => t.id === platformAdminTenantId
  );
  const studioName = isPlatformAdmin
    ? prettyStudio(pickedTenant?.studioId)
    : prettyStudio(session?.studioId);
  return (
    <>
      <PageHead
        eyebrow="Organization · settings"
        title={
          <>
            Configure <em>{studioName}.</em>
          </>
        }
        sub="Connectors, branding, security, and role-based access for everyone in this organization. Changes are scoped to your organization only."
        cta={
          <span className="set__role">
            <span className="dot" />
            {prettyRole(session?.role)} · {session?.displayName ?? "—"}
          </span>
        }
      />

      {isPlatformAdmin && (
        <div className="set__tenant-picker" data-testid="set-tenant-picker">
          <span className="set__tenant-picker__lbl">
            Viewing as platform admin — choose an organization:
          </span>
          <Select
            label="Organization"
            value={
              platformAdminTenantId.length > 0
                ? platformAdminTenantId
                : undefined
            }
            onValueChange={(v) => setPlatformAdminTenantId(v ?? "")}
            options={(tenantsListQuery.data?.tenants ?? []).map((t) => ({
              value: t.id,
              label: prettyStudio(t.studioId),
            }))}
            placeholder="Select an organization"
            clearable={false}
            disabled={tenantsListQuery.isLoading}
          />
        </div>
      )}

      <div className="set__split">
        <SectionNav active={active} onSelect={selectSection} />
        <div className="set__main">
          <Panel id="general" active={active}>
            <GeneralSection
              studioName={studioName}
              tenantId={tenantId}
              tenant={tenantQuery.data ?? null}
              isLoading={tenantQuery.isLoading}
              isError={tenantQuery.isError}
            />
          </Panel>
          <Panel id="connectors" active={active}>
            <ConnectorsSection tenantId={tenantId} />
          </Panel>
          <Panel id="precedent" active={active}>
            <PrecedentEngineSection tenantId={tenantId} />
          </Panel>
          <Panel id="precedentAnalytics" active={active}>
            <PrecedentAnalyticsSection tenantId={tenantId} />
          </Panel>
          <Panel id="features" active={active}>
            {/* Rendered only while this tab is open, unlike its siblings.
                Panel keeps children mounted and merely `hidden`, so an
                always-rendered section would fire its GET on every Settings
                mount — and put its switches in the accessibility tree of every
                other tab, where they aren't visible but are findable. */}
            {active === "features" ? (
              <FeatureSwitchesSection tenantId={tenantId} />
            ) : null}
          </Panel>
          <Panel id="look" active={active}>
            <LookFeelSection tenantId={tenantId} />
          </Panel>
          <Panel id="security" active={active}>
            <SecuritySection
              tenantId={tenantId}
              policy={securityQuery.data ?? null}
              isLoading={securityQuery.isLoading}
              isError={securityQuery.isError}
            />
          </Panel>
          <Panel id="rbac" active={active}>
            <RbacSection
              tenantId={tenantId}
              users={usersQuery.data?.users ?? null}
            />
          </Panel>
          <Panel id="members" active={active}>
            <MembersSection
              tenantId={tenantId}
              users={usersQuery.data?.users ?? null}
              isLoading={usersQuery.isLoading}
              isFetching={usersQuery.isFetching}
              isError={usersQuery.isError}
            />
          </Panel>
          <Panel id="apikeys" active={active}>
            <ApiKeysSection tenantId={tenantId} />
          </Panel>
          <Panel id="mcp" active={active}>
            <McpAccessSection tenantId={tenantId} />
          </Panel>
          <Panel id="notifications" active={active}>
            <NotificationsSection tenantId={tenantId} />
          </Panel>
          <Panel id="data" active={active}>
            <DataSection tenantId={tenantId} />
          </Panel>
          <SaveBar />
        </div>
      </div>
    </>
  );
}

function SectionNav({
  active,
  onSelect,
}: {
  active: SectionId;
  onSelect: (id: SectionId) => void;
}): JSX.Element {
  return (
    <aside className="set__nav" role="tablist" aria-label="Settings sections">
      <div className="set__nav__lbl">Sections</div>
      {SECTION_IDS.map((id) => {
        const on = active === id;
        return (
          <a
            key={id}
            href={`#${id}`}
            role="tab"
            aria-selected={on}
            aria-controls={`set-panel-${id}`}
            className={on ? "is-on" : undefined}
            data-testid={`set-tab-${id}`}
            onClick={(e) => {
              e.preventDefault();
              onSelect(id);
            }}
          >
            {SECTION_LABELS[id]}
          </a>
        );
      })}
    </aside>
  );
}

// One section per tab. Inactive panels render `hidden` (display:none) so they
// stay mounted — preserving each section's in-flight form state and query
// cache when you switch away — while dropping out of the layout and the
// accessibility tree, exactly like the wireframe's tab behaviour.
function Panel({
  id,
  active,
  children,
}: {
  id: SectionId;
  active: SectionId;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      id={`set-panel-${id}`}
      role="tabpanel"
      aria-labelledby={`set-tab-${id}`}
      className="set__panel"
      hidden={active !== id}
    >
      {children}
    </div>
  );
}

// S-095g — region and time zone live as live Tenant fields per the
// design handoff §3.4 "Data & retention" rollup. Region is read-only
// (the wireframe says "Cannot be changed after first ingest"); time
// zone is editable with a per-section save, mirroring SecuritySection.
const REGION_OPTIONS: ReadonlyArray<{
  value: TenantRow["region"];
  label: string;
}> = [
  { value: "us-west-2", label: "us-west-2 (Oregon)" },
  { value: "us-east-1", label: "us-east-1 (Virginia)" },
  { value: "eu-west-1", label: "eu-west-1 (Ireland)" },
];

const TIME_ZONE_OPTIONS: ReadonlyArray<{
  value: TenantTimeZone;
  label: string;
}> = [
  { value: "America/Los_Angeles", label: "America/Los_Angeles (PT)" },
  { value: "America/New_York", label: "America/New_York (ET)" },
  { value: "UTC", label: "UTC" },
];

function GeneralSection({
  studioName,
  tenantId,
  tenant,
  isLoading,
  isError,
}: {
  studioName: string;
  tenantId: string;
  tenant: TenantRow | null;
  isLoading: boolean;
  isError: boolean;
}): JSX.Element {
  // S-095a wires the read paths. Tenant identity (name, ID,
  // accessTier) is read-only by design — name + accessTier changes
  // happen out-of-band via platform_admin per the original S-095 spec.
  // S-095g wires region (read-only) + time zone (editable). The mutation
  // is scoped to time zone only; region edits happen via an out-of-band
  // ticket per the wireframe hint.
  const [timeZone, setTimeZone] = useState<TenantTimeZone>("America/Los_Angeles");
  const mutation = useUpdateTenantTimeZone(tenantId);

  useEffect(() => {
    if (tenant === null) return;
    setTimeZone(tenant.timeZone);
  }, [tenant]);

  const tzDirty = tenant !== null && tenant.timeZone !== timeZone;
  const canSaveTz =
    tzDirty &&
    tenantId.length > 0 &&
    !isLoading &&
    !isError &&
    !mutation.isPending;
  const onSaveTz = (): void => {
    mutation.mutate(timeZone);
  };

  return (
    <section className="set__sec" data-testid="set-general">
      <SectionHead
        id="general"
        title="General"
        sub="Tenant identity. These appear in audit exports, plugin headers, and email notifications."
        action={
          <Button
            variant="primary"
            className="btn--s"
            onClick={onSaveTz}
            disabled={!canSaveTz}
            data-testid="set-general-time-zone-save"
          >
            {mutation.isPending ? "Saving…" : "Save time zone"}
          </Button>
        }
      />
      {mutation.isError && (
        <div
          className="set__sec__err"
          data-testid="set-general-time-zone-error"
          role="alert"
        >
          Could not save time zone — please retry.
        </div>
      )}
      <div className="set__row">
        <div className="set__row__k">
          Tenant name
          <em>Display name shown in plugin and on dashboards.</em>
        </div>
        <div className="set__row__v">
          <input
            className="set__inp"
            value={studioName}
            readOnly
            disabled
            data-testid="set-general-name"
          />
        </div>
      </div>
      <div className="set__row">
        <div className="set__row__k">
          Tenant ID
          <em>Read-only — used in audit logs and SSO.</em>
        </div>
        <div className="set__row__v">
          <input
            className="set__inp"
            value={tenantValueOrPlaceholder(tenant?.id, isLoading, isError)}
            readOnly
            disabled
            style={{ fontFamily: "var(--cs-font-mono)" }}
            data-testid="set-general-id"
          />
        </div>
      </div>
      <div className="set__row">
        <div className="set__row__k">
          Access tier
          <em>
            Determines which MCP tools and admin surfaces this organization
            can use. Tier changes happen out-of-band via platform_admin.
          </em>
        </div>
        <div className="set__row__v">
          <input
            className="set__inp"
            value={tenantValueOrPlaceholder(
              tenant?.accessTier,
              isLoading,
              isError
            )}
            readOnly
            disabled
            data-testid="set-general-tier"
          />
        </div>
      </div>
      <div className="set__row">
        <div className="set__row__k">
          Deployment mode
          <em>
            SaaS, hybrid, or on-prem. Drives ingestion routing and where
            content is processed.
          </em>
        </div>
        <div className="set__row__v">
          <input
            className="set__inp"
            value={tenantValueOrPlaceholder(
              tenant?.deploymentMode,
              isLoading,
              isError
            )}
            readOnly
            disabled
            data-testid="set-general-deployment-mode"
          />
        </div>
      </div>
      <div className="set__row">
        <div className="set__row__k">
          Default region
          <em>Where indexes and audit chains are stored.</em>
        </div>
        <div className="set__row__v">
          <Select<TenantRow["region"]>
            label="Default region"
            value={tenant?.region ?? "us-west-2"}
            onValueChange={() => undefined}
            disabled
            clearable={false}
            options={REGION_OPTIONS}
          />
          <span className="set__hint">
            Cannot be changed after first ingest. Open a ticket to migrate.
          </span>
        </div>
      </div>
      <div className="set__row">
        <div className="set__row__k">
          Time zone
          <em>Used for date display and scheduled re-attestations.</em>
        </div>
        <div className="set__row__v">
          <Select<TenantTimeZone>
            label="Time zone"
            value={timeZone}
            onValueChange={(v) => {
              if (v !== undefined) setTimeZone(v);
            }}
            disabled={isLoading || isError || tenant === null || mutation.isPending}
            clearable={false}
            options={TIME_ZONE_OPTIONS}
          />
        </div>
      </div>
    </section>
  );
}

function tenantValueOrPlaceholder(
  value: string | undefined,
  isLoading: boolean,
  isError: boolean
): string {
  if (isError) return "unavailable";
  if (isLoading || value === undefined) return "loading…";
  return value;
}

// [S-303] Connectors section — supersedes the S-244 NotYetBuilt
// placeholder. Real Google Drive flow: Connect → register folder →
// Sync Now. Dropbox / SharePoint / etc. stay parked until an organization
// asks (still surfaced in the sub-copy so users know what's NOT here).
// [S-603] Precedent engine — data-residency connector (E-L2-RESIDENCY).
// "Our engine, your environment": the tenant keeps their Layer-2 precedent
// corpus resident in their own cloud; ContractStudio still owns the embeddings,
// extraction, and synthesis (design §8a — never "your own engine"). Gate-locked
// behind FEATURE_FLAG_HYBRID_ONPREM (Q4 2026): "Managed" is the real current
// state; "Resident" reveals a connector whose Test/Save hit a 501 stub and
// surface an honest "Pending backend: S-600" — no fake success.
const PE_BACKEND_OPTIONS: ReadonlyArray<{
  value: PrecedentEngineConnector["backend"];
  label: string;
}> = [
  { value: "pgvector", label: "pgvector (Cloud SQL / AlloyDB)" },
  { value: "firestore-remote", label: "Firestore-in-tenant (dogfood)" },
  { value: "vertex-vs", label: "Vertex Vector Search" },
];

// [S-614] Cloud-agnostic blob-storage providers for resident uploaded files.
const PE_STORAGE_PROVIDER_OPTIONS: ReadonlyArray<{
  value: PrecedentStorageProvider;
  label: string;
}> = [
  { value: "gcs", label: "Google Cloud Storage" },
  { value: "s3", label: "Amazon S3" },
  { value: "azure-blob", label: "Azure Blob Storage" },
];

function PrecedentEngineSection({
  tenantId,
}: {
  tenantId: string;
}): JSX.Element {
  const [mode, setMode] = useState<PrecedentEngineMode>("managed");
  const [backend, setBackend] =
    useState<PrecedentEngineConnector["backend"]>("pgvector");
  const [endpoint, setEndpoint] = useState("");
  const [namespace, setNamespace] = useState("");
  // [S-614] Resident blob storage for uploaded files — captured alongside the
  // precedent store. Off by default → files stay in the managed cloud.
  const [storageResident, setStorageResident] = useState(false);
  const [storageProvider, setStorageProvider] =
    useState<PrecedentStorageProvider>("gcs");
  const [storageBucket, setStorageBucket] = useState("");
  const [storagePrefix, setStoragePrefix] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [connected, setConnected] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const testConn = useTestPrecedentEngineConnection();
  const saveConfig = useSavePrecedentEngineConfig();
  // [S-643 follow-up] Reclassify documentForm on already-ingested precedents
  // — applies regardless of managed/resident mode, so it's rendered outside
  // the mode === "resident" branch below. Async + polled (mirrors the
  // Precedent Analytics populate flow, S-617): found live that a bare
  // button-spinner gave no way to tell a long scan apart from a hang.
  const startBackfill = useStartDocumentFormBackfill(tenantId);
  const [backfillPolling, setBackfillPolling] = useState(false);
  const backfillStatusQuery = useDocumentFormBackfillStatus(
    tenantId,
    backfillPolling
  );
  const [backfillStartError, setBackfillStartError] = useState<string | null>(
    null
  );
  // Pick up an already-running or previously-completed run on load (e.g. a
  // revisit mid-scan, or after a page refresh) instead of only reacting to
  // this session's own button click.
  const initialBackfillStatusQuery = useDocumentFormBackfillStatus(
    tenantId,
    !backfillPolling
  );
  useEffect(() => {
    if (initialBackfillStatusQuery.data?.status === "running") {
      setBackfillPolling(true);
    }
  }, [initialBackfillStatusQuery.data?.status]);
  const backfillRun = backfillPolling
    ? backfillStatusQuery.data
    : initialBackfillStatusQuery.data;
  const backfillRunning = backfillRun?.status === "running";
  const cancelBackfill = useCancelDocumentFormBackfill();

  // Hydrate from the persisted config so a saved "Resident" selection survives a
  // revisit (S-609). Apply once, when it first loads, so a background refetch
  // never clobbers in-progress edits.
  const configQuery = usePrecedentEngineConfig(tenantId);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated || configQuery.data === undefined) return;
    const c = configQuery.data;
    setMode(c.mode);
    if (c.connector !== undefined) {
      setBackend(c.connector.backend);
      setEndpoint(c.connector.endpoint);
      setNamespace(c.connector.namespace);
      if (c.connector.storage !== undefined) {
        setStorageResident(true);
        setStorageProvider(c.connector.storage.provider);
        setStorageBucket(c.connector.storage.bucket);
        setStoragePrefix(c.connector.storage.prefix ?? "");
      }
    }
    setHydrated(true);
  }, [hydrated, configQuery.data]);

  const connector: PrecedentEngineConnector = {
    backend,
    endpoint: endpoint.trim(),
    embeddingModelId: "text-embedding-004",
    dimensions: 768,
    namespace: namespace.trim(),
    ...(storageResident && storageBucket.trim().length > 0
      ? {
          storage: {
            provider: storageProvider,
            bucket: storageBucket.trim(),
            ...(storagePrefix.trim().length > 0
              ? { prefix: storagePrefix.trim() }
              : {}),
          },
        }
      : {}),
  };

  const resetFlashes = (): void => {
    setFormError(null);
    setPending(null);
    setConnected(null);
    setSaved(null);
  };

  const onTest = async (): Promise<void> => {
    resetFlashes();
    if (endpoint.trim().length === 0) {
      setFormError("Enter your store endpoint first.");
      return;
    }
    if (namespace.trim().length === 0) {
      setFormError("Enter the namespace for your resident store.");
      return;
    }
    try {
      // Gate open → this is a live canary round-trip against the resident
      // store (S-603). Gate closed → the backend returns 501 and we fall to
      // the honest "Pending backend" state below.
      const res = await testConn.mutateAsync({ tenantId, connector });
      setConnected(
        `Connected — embeddings verified (${res.embeddingModelId ?? "text-embedding-004"} · ${res.dimensions ?? 768} dims).`
      );
    } catch (err) {
      // 501 (gate closed) OR a real canary failure (422 canary_failed /
      // 409 tenant_conflict / 502 store_unreachable) — surface the message,
      // never a fake success.
      setPending(
        getApiErrorMessage(err) ?? "Pending backend: S-600 / S-601 / S-602."
      );
    }
  };

  const onSave = async (): Promise<void> => {
    resetFlashes();
    try {
      await saveConfig.mutateAsync({
        tenantId,
        mode,
        connector: mode === "resident" ? connector : undefined,
      });
      setSaved(
        mode === "resident"
          ? "Saved — resident precedent engine is now active for this studio."
          : "Saved — precedents stay in the ContractStudio-managed cloud."
      );
    } catch (err) {
      setPending(getApiErrorMessage(err) ?? "Pending backend: S-600.");
    }
  };

  const onBackfillDocumentForm = async (): Promise<void> => {
    setBackfillStartError(null);
    try {
      await startBackfill.mutateAsync({});
      setBackfillPolling(true);
    } catch (err) {
      setBackfillStartError(
        getApiErrorMessage(err) ?? "Couldn't reclassify precedents."
      );
    }
  };

  const onCancelBackfill = async (): Promise<void> => {
    // Best-effort — stopping isn't instant, the existing poll picks up the
    // eventual "cancelled" status on its own. A failed cancel request isn't
    // worth its own error banner; the Stop button just stays available to
    // retry.
    try {
      await cancelBackfill.mutateAsync({});
    } catch {
      // intentionally swallowed — see comment above
    }
  };

  return (
    <section className="set__sec" data-testid="set-precedent">
      <SectionHead
        id="precedent"
        story="S-603"
        title={
          <>
            Precedent engine <em>· data residency</em>
          </>
        }
        sub="Keep your studio's Layer-2 precedent corpus resident in your own cloud. This is the ContractStudio engine deployed in your environment — you bring the source-of-truth documents and the residency boundary; we build the contract-tuned embeddings, extraction, and synthesis. Gate-locked until Q4 2026."
      />

      <div className="set__connector__panel">
        <div
          className="set__pe__modes"
          role="radiogroup"
          aria-label="Precedent engine mode"
        >
          <label className="set__pe__mode">
            <input
              type="radio"
              name="pe-mode"
              checked={mode === "managed"}
              onChange={() => setMode("managed")}
              data-testid="pe-mode-managed"
            />
            <span>
              <b>Managed by ContractStudio</b>
              <span className="set__connector__sub">
                Your precedents live in our managed cloud — the default today.
              </span>
            </span>
          </label>
          <label className="set__pe__mode">
            <input
              type="radio"
              name="pe-mode"
              checked={mode === "resident"}
              onChange={() => setMode("resident")}
              data-testid="pe-mode-resident"
            />
            <span>
              <b>Resident in your cloud</b>
              <span className="set__connector__sub">
                You provision the store + embedding endpoint; our engine
                reads/writes it. Your data-at-rest stays in your cloud.
              </span>
            </span>
          </label>
        </div>

        {/* [S-643 follow-up] Applies regardless of managed/resident mode —
            reclassifies already-ingested precedents (deal-memo vs. long-form)
            from their stored clauses. New uploads classify automatically
            since Stage 2; this is the catch-up for everything ingested
            before that shipped. Async + polled — see the hooks above. */}
        <div className="set__pe__backfill" data-testid="pe-backfill">
          <div className="set__connector__sub">
            <b>Document form classification.</b> Reclassify already-ingested
            precedents as deal-memo (short form) or long-form from their
            extracted clauses. Safe to re-run — already-classified records are
            skipped.
          </div>
          {backfillRunning ? (
            <div
              className="set__pe__backfill__progress"
              role="status"
              data-testid="pe-backfill-progress"
            >
              <span>
                Reclassifying… {backfillRun.processed} /{" "}
                {backfillRun.total || "?"}
              </span>
              <Button
                variant="secondary"
                className="btn--s"
                onClick={() => void onCancelBackfill()}
                loading={cancelBackfill.isPending}
                data-testid="pe-backfill-cancel"
              >
                Stop
              </Button>
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={() => void onBackfillDocumentForm()}
              loading={startBackfill.isPending}
              data-testid="pe-backfill-run"
            >
              {startBackfill.isPending
                ? "Starting…"
                : backfillRun !== null && backfillRun !== undefined
                  ? "Reclassify again"
                  : "Reclassify document forms"}
            </Button>
          )}
          {backfillRun?.status === "completed" && (
            <div
              className="set__connector__flash set__connector__flash--ok"
              role="status"
              data-testid="pe-backfill-result"
            >
              Last run: classified {backfillRun.classified} · unclassifiable{" "}
              {backfillRun.unclassifiable} · already classified{" "}
              {backfillRun.skippedAlreadyClassified} · no clauses to classify
              from {backfillRun.skippedNoClauseRecords} · failed{" "}
              {backfillRun.failed}.
            </div>
          )}
          {backfillRun?.status === "failed" && (
            <div
              className="set__connector__flash set__connector__flash--err"
              role="alert"
              data-testid="pe-backfill-error"
            >
              Reclassification failed — {backfillRun.error}
            </div>
          )}
          {backfillRun?.status === "cancelled" && (
            <div className="set__connector__notice" role="status" data-testid="pe-backfill-cancelled">
              Stopped — classified {backfillRun.classified} of{" "}
              {backfillRun.total || "?"} before stopping. Already-classified
              precedents keep their classification; reclassify again to
              finish the rest.
            </div>
          )}
          {backfillStartError !== null && (
            <div
              className="set__connector__notice"
              role="status"
              data-testid="pe-backfill-start-error"
            >
              {backfillStartError}
            </div>
          )}
        </div>

        {mode === "resident" && (
          <>
            <div
              className="set__connector__notice"
              role="status"
              data-testid="pe-gate-notice"
            >
              This is gated behind <code>FEATURE_FLAG_HYBRID_ONPREM</code> and
              its backend isn&rsquo;t built yet — connecting a resident store
              lands with the routing seam and resident adapter.{" "}
              <span className="set__connector__story">S-600</span>
            </div>

            <div className="set__connector__addform">
              <Select<PrecedentEngineConnector["backend"]>
                label="Backend"
                value={backend}
                onValueChange={(v) => {
                  if (v !== undefined) setBackend(v);
                }}
                clearable={false}
                options={PE_BACKEND_OPTIONS}
              />
              <label className="set__connector__field">
                <span>Store endpoint</span>
                <input
                  type="text"
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  placeholder="e.g. 10.20.0.5:5432/precedents"
                  data-testid="pe-endpoint"
                />
              </label>
              <label className="set__connector__field">
                <span>Embedding model · dimensions</span>
                <input
                  type="text"
                  value="text-embedding-004 · 768"
                  readOnly
                  disabled
                  title="We own the embedding model + dims and re-embed against them (design §5.4)."
                  data-testid="pe-embedding"
                />
              </label>
              <label className="set__connector__field">
                <span>Namespace</span>
                <input
                  type="text"
                  value={namespace}
                  onChange={(e) => setNamespace(e.target.value)}
                  placeholder={`studio-${tenantId || "your-studio"}`}
                  data-testid="pe-namespace"
                />
              </label>

              {/* [S-614] Resident blob storage for uploaded files — same
                  section as the precedent store, since residency of the corpus
                  and residency of the raw files are one decision. */}
              <div className="set__pe__storage" data-testid="pe-storage">
                <Checkbox
                  id="pe-storage-resident"
                  checked={storageResident}
                  onCheckedChange={setStorageResident}
                  label="Store uploaded files in your cloud too"
                  aria-label="Store uploaded files in your cloud too"
                  data-testid="pe-storage-toggle"
                />
                <span className="set__connector__sub">
                  Off &rarr; uploaded files stay in the ContractStudio-managed
                  cloud even when precedents are resident (partial residency).
                </span>
                {storageResident && (
                  <>
                    <Select<PrecedentStorageProvider>
                      label="Storage provider"
                      value={storageProvider}
                      onValueChange={(v) => {
                        if (v !== undefined) setStorageProvider(v);
                      }}
                      clearable={false}
                      options={PE_STORAGE_PROVIDER_OPTIONS}
                    />
                    <label className="set__connector__field">
                      <span>Bucket / container</span>
                      <input
                        type="text"
                        value={storageBucket}
                        onChange={(e) => setStorageBucket(e.target.value)}
                        placeholder="e.g. acme-contract-uploads"
                        data-testid="pe-storage-bucket"
                      />
                    </label>
                    <label className="set__connector__field">
                      <span>Path prefix (optional)</span>
                      <input
                        type="text"
                        value={storagePrefix}
                        onChange={(e) => setStoragePrefix(e.target.value)}
                        placeholder="e.g. contractstudio/uploads"
                        data-testid="pe-storage-prefix"
                      />
                    </label>
                    <span className="set__connector__story">
                      Captured now; uploads route to your bucket with S-614.
                    </span>
                  </>
                )}
              </div>

              <div className="set__pe__actions">
                <Button
                  variant="secondary"
                  onClick={() => void onTest()}
                  loading={testConn.isPending}
                  data-testid="pe-test"
                >
                  Test connection
                </Button>
                <Button
                  onClick={() => void onSave()}
                  loading={saveConfig.isPending}
                  data-testid="pe-save"
                >
                  Save
                </Button>
              </div>
            </div>
          </>
        )}

        {connected !== null && (
          <div
            className="set__connector__flash set__connector__flash--ok"
            role="status"
            data-testid="pe-connected"
          >
            {connected}
          </div>
        )}
        {saved !== null && (
          <div
            className="set__connector__flash set__connector__flash--ok"
            role="status"
            data-testid="pe-saved"
          >
            {saved}
          </div>
        )}
        {formError !== null && (
          <div
            className="set__connector__flash set__connector__flash--err"
            role="alert"
          >
            {formError}
          </div>
        )}
        {pending !== null && (
          <div
            className="set__connector__notice"
            role="status"
            data-testid="pe-pending"
          >
            <b>Pending backend.</b> {pending}
          </div>
        )}
      </div>
    </section>
  );
}

function ConnectorsSection({ tenantId }: { tenantId: string }): JSX.Element {
  // [S-269] Is Drive actually wired on this environment? Gate everything on it
  // so the UI degrades honestly instead of 404-ing behind the Connect button.
  const statusQuery = useDriveConnectorStatus();
  const driveConfigured = statusQuery.data?.drive?.configured;
  const connectorsQuery = useConnectors(tenantId, {
    enabled: driveConfigured === true,
  });
  const startOAuth = useStartDriveOAuth();
  const registerFolder = useRegisterDriveFolder();
  const syncConnector = useSyncDriveConnector();
  const deleteConnector = useDeleteDriveConnector();

  // OAuth-callback flash banner. The admin oauth-callback handler
  // redirects back to /settings#connectors with either
  // `?drive_connected=1` or `?drive_error=<code>`. We strip the param
  // after rendering so a refresh doesn't replay the banner.
  const [searchParams, setSearchParams] = useSearchParams();
  const connectedFlash = searchParams.get("drive_connected") === "1";
  const errorFlash = searchParams.get("drive_error");
  useEffect(() => {
    if (!connectedFlash && errorFlash === null) return;
    const next = new URLSearchParams(searchParams);
    next.delete("drive_connected");
    next.delete("drive_error");
    setSearchParams(next, { replace: true });
  }, [connectedFlash, errorFlash, searchParams, setSearchParams]);

  const [folderInput, setFolderInput] = useState("");
  const [displayNameInput, setDisplayNameInput] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const connectors = connectorsQuery.data ?? [];
  const isLoading = connectorsQuery.isLoading;
  const hasConnectedAccount = useMemo(
    () => connectors.length > 0,
    [connectors]
  );

  const onConnect = async (): Promise<void> => {
    setFormError(null);
    try {
      const { authorizeUrl } = await startOAuth.mutateAsync({ tenantId });
      // Full-page redirect to Google's consent screen; the admin
      // callback handler will redirect back to /settings#connectors
      // with drive_connected=1 or drive_error=…
      window.location.assign(authorizeUrl);
    } catch (err) {
      setFormError(
        getApiErrorMessage(err) ??
          "Couldn't start the Google Drive consent flow."
      );
    }
  };

  const onRegister = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    if (folderInput.trim().length === 0) {
      setFormError("Paste a Drive folder URL or id.");
      return;
    }
    if (displayNameInput.trim().length === 0) {
      setFormError("Give this folder a friendly name.");
      return;
    }
    try {
      await registerFolder.mutateAsync({
        tenantId,
        folderId: folderInput.trim(),
        displayName: displayNameInput.trim(),
      });
      setFolderInput("");
      setDisplayNameInput("");
    } catch (err) {
      const msg = getApiErrorMessage(err);
      if (msg !== null && msg.toLowerCase().includes("connect your google drive")) {
        setFormError(
          "Connect your Google Drive account first using the button above."
        );
      } else {
        setFormError(msg ?? "Couldn't register that folder.");
      }
    }
  };

  return (
    <section className="set__sec" data-testid="set-connectors">
      <SectionHead
        id="connectors"
        title={
          <>
            Connectors <em>· precedent sources</em>
          </>
        }
        sub="Bring your studio's contract history into your organization. Google Drive is wired today; Dropbox and SharePoint stay parked until an organization asks for them. Templates are managed centrally and is not affected by these."
      />

      {connectedFlash ? (
        <div className="set__connector__flash set__connector__flash--ok" role="status">
          Google Drive connected. Add a folder below to start syncing.
        </div>
      ) : null}
      {errorFlash !== null ? (
        <div className="set__connector__flash set__connector__flash--err" role="alert">
          Google Drive connection failed: <code>{errorFlash}</code>. Try
          again, or revoke prior access at{" "}
          <a
            href="https://myaccount.google.com/permissions"
            target="_blank"
            rel="noreferrer noopener"
          >
            myaccount.google.com/permissions
          </a>{" "}
          and retry.
        </div>
      ) : null}

      <div className="set__connector__panel">
        <div className="set__connector__row">
          <div>
            <div className="set__connector__heading">Google Drive</div>
            <div className="set__connector__sub">
              Read-only. We import each new file in the folder as a draft
              template, auto-tag it, and you certify in{" "}
              <Link to="/template-management">/template-management</Link>.
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={() => void onConnect()}
            disabled={startOAuth.isPending || driveConfigured !== true}
            title={
              driveConfigured === false
                ? "Google Drive app isn't registered yet — once it is, you'll connect with your own Google account (S-269)"
                : undefined
            }
            data-testid="connectors-connect-drive"
          >
            {hasConnectedAccount
              ? "Reconnect Google Drive"
              : "Connect Google Drive"}
          </Button>
        </div>

        {/* [S-269] Honest degrade: when the connector isn't wired on this
            environment, say so with the story id instead of letting the button
            401/404 into a generic error. The blocker is a one-time app
            registration — NOT anything per user; each user connects with their
            own Google account once the app is registered. */}
        {driveConfigured === false && (
          <div
            className="set__connector__notice"
            role="status"
            data-testid="connectors-not-configured"
          >
            Google Drive isn't available yet — the ContractStudio app still needs
            to be registered with Google (a one-time platform setup). Once it is,
            you'll connect with <b>your own Google account</b> right here.{" "}
            <span className="set__connector__story">S-269</span>
          </div>
        )}

        {/* Folder registration + folder list only make sense once Drive is
            configured and connected — hidden on unconfigured environments so we
            never show a form that can't work (S-269). */}
        {driveConfigured === true && (
          <>
            <form
              className="set__connector__addform"
              onSubmit={(e) => void onRegister(e)}
            >
              <label className="set__connector__field">
                <span>Drive folder URL or id</span>
                <input
                  type="text"
                  value={folderInput}
                  onChange={(e) => setFolderInput(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/…"
                  data-testid="connectors-folder-input"
                />
              </label>
              <label className="set__connector__field">
                <span>Friendly name</span>
                <input
                  type="text"
                  value={displayNameInput}
                  onChange={(e) => setDisplayNameInput(e.target.value)}
                  placeholder="e.g. Q2 templates"
                  data-testid="connectors-name-input"
                />
              </label>
              <Button
                type="submit"
                disabled={registerFolder.isPending}
                data-testid="connectors-register"
              >
                {registerFolder.isPending ? "Registering…" : "Register folder"}
              </Button>
            </form>

            {formError !== null ? (
              <div className="set__connector__flash set__connector__flash--err" role="alert">
                {formError}
              </div>
            ) : null}

            {isLoading ? (
              <div className="set__connector__hint"><Spinner size={13} /> Loading folders…</div>
            ) : connectors.length === 0 ? (
              <div className="set__connector__hint">
                No folders registered yet. Connect Google Drive and add a folder.
              </div>
            ) : (
              <ul className="set__connector__list" data-testid="connectors-list">
                {connectors.map((c) => (
                  <ConnectorRowItem
                    key={c.connectorId}
                    connector={c}
                    onSync={() =>
                      syncConnector.mutate({
                        tenantId,
                        connectorId: c.connectorId,
                      })
                    }
                    onDelete={() =>
                      deleteConnector.mutate({
                        tenantId,
                        connectorId: c.connectorId,
                      })
                    }
                    isSyncing={
                      syncConnector.isPending &&
                      syncConnector.variables?.connectorId === c.connectorId
                    }
                    isDeleting={
                      deleteConnector.isPending &&
                      deleteConnector.variables?.connectorId === c.connectorId
                    }
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function ConnectorRowItem({
  connector,
  onSync,
  onDelete,
  isSyncing,
  isDeleting,
}: {
  connector: ConnectorRow;
  onSync: () => void;
  onDelete: () => void;
  isSyncing: boolean;
  isDeleting: boolean;
}): JSX.Element {
  const counts = connector.lastSyncCounts;
  return (
    <li
      className="set__connector__item"
      data-testid={`connector-${connector.connectorId}`}
    >
      <div className="set__connector__item__head">
        <div>
          <div className="set__connector__item__name">
            {connector.displayName}
          </div>
          <div className="set__connector__item__meta">
            Folder <code>{connector.folderId}</code> · connected{" "}
            {formatShortDate(connector.connectedAt)}
          </div>
        </div>
        <div className="set__connector__item__actions">
          <Button
            variant="secondary"
            onClick={onSync}
            disabled={isSyncing}
            data-testid={`connector-sync-${connector.connectorId}`}
          >
            {isSyncing ? "Syncing…" : "Sync now"}
          </Button>
          <Button
            variant="ghost"
            onClick={onDelete}
            disabled={isDeleting}
            data-testid={`connector-delete-${connector.connectorId}`}
          >
            {isDeleting ? "Removing…" : "Disconnect"}
          </Button>
        </div>
      </div>
      <div className="set__connector__item__status">
        {connector.lastSyncAt === null ? (
          <em>Not synced yet.</em>
        ) : (
          <>
            Last synced {formatShortDate(connector.lastSyncAt)}.{" "}
            {counts !== null ? (
              <>
                <strong>{counts.imported}</strong> imported,{" "}
                {counts.skippedExisting} already in library,{" "}
                {counts.skippedUntaggable} skipped (auto-tag failed),{" "}
                {counts.errored} errored.
              </>
            ) : null}
          </>
        )}
        {connector.lastSyncError !== null ? (
          <div className="set__connector__item__error">
            Last error: {connector.lastSyncError}
          </div>
        ) : null}
      </div>
    </li>
  );
}

// Feature switches — the tenant-admin control for features that used to be
// gated on process env vars set in two different files (scripts/dev-up.sh
// locally, apphosting.yaml + deploy-staging.yml deployed). The switch state is
// ONE Firestore record per tenant that every environment reads, so a switch
// means the same thing on a laptop and in production — which the env vars
// demonstrably did not (Review Engine V2 ran in prod while the deployed admin
// skipped the taxonomy measurement V2 aligns by).
//
// The list is server-driven: the backend returns every flag in its registry
// with a label and description, so adding a switch needs no change here.
function FeatureSwitchesSection({ tenantId }: { tenantId: string }): JSX.Element {
  const flagsQuery = useFeatureFlags(tenantId);
  const setFlag = useSetFeatureFlag(tenantId);
  const [error, setError] = useState<string | null>(null);
  // Which key is mid-flight — so only the switch being changed goes busy,
  // not every switch on the page.
  const [flipping, setFlipping] = useState<string | null>(null);

  const handleToggle = async (key: string, next: boolean): Promise<void> => {
    setError(null);
    setFlipping(key);
    try {
      await setFlag.mutateAsync({ key, enabled: next });
    } catch (err) {
      setError(getApiErrorMessage(err) ?? "Couldn't change that switch.");
    } finally {
      setFlipping(null);
    }
  };

  const flags = flagsQuery.data?.flags ?? [];

  return (
    <section className="set__sec" data-testid="set-features">
      <SectionHead
        id="features"
        title={
          <>
            Feature switches <em>· same in every environment</em>
          </>
        }
        sub="Turn platform features on or off for your studio. A switch takes effect on the next request — there is nothing to redeploy — and it is the same setting whether the work runs locally or in production."
      />
      {flagsQuery.isPending ? (
        <div className="set__row">
          <div className="set__row__k">Loading switches…</div>
        </div>
      ) : null}
      {flagsQuery.isError ? (
        <div className="set__row">
          <div className="set__row__k">
            Couldn't load feature switches
            <em>{flagsQuery.error.message}</em>
          </div>
        </div>
      ) : null}
      {flags.map((flag) => (
        <div className="set__row" key={flag.key}>
          <div className="set__row__k">
            {flag.label}
            <em>
              {flag.description}
              {!flag.explicitlySet
                ? " Currently on the platform default — nobody has set this for your studio yet."
                : ""}
            </em>
          </div>
          <div className="set__row__v">
            <Switch
              checked={flag.enabled}
              onCheckedChange={(next) => void handleToggle(flag.key, next)}
              disabled={flipping === flag.key}
              label={flag.enabled ? "On" : "Off"}
            />
          </div>
        </div>
      ))}
      {error !== null ? (
        <div className="set__row">
          <div className="set__row__k">
            <em>{error}</em>
          </div>
        </div>
      ) : null}
    </section>
  );
}

// [S-617] Precedent Analytics — the Settings toggle for the structured
// measures store. Turning this ON provisions a dedicated, isolated Postgres
// schema for the studio (idempotent — safe if it's already provisioned).
// Turning it OFF only flips the flag; it never drops the schema or its
// data. Gate-locked (no measures Postgres instance is provisioned anywhere
// yet — POST enable/disable return 501), same honest-501 posture as
// PrecedentEngineSection above.
const DEFAULT_ANALYTICS_WINDOW_MONTHS = 36;

function PrecedentAnalyticsSection({
  tenantId,
}: {
  tenantId: string;
}): JSX.Element {
  const configQuery = usePrecedentAnalyticsConfig(tenantId);
  const enable = useEnablePrecedentAnalytics(tenantId);
  const disable = useDisablePrecedentAnalytics(tenantId);
  const [pending, setPending] = useState<string | null>(null);

  const status: PrecedentAnalyticsStatus = configQuery.data?.status ?? "disabled";
  // Explicit `false` only — an older backend that doesn't send the field, or a
  // response still in flight, must not be reported as broken.
  const notConfigured = configQuery.data?.configured === false;
  const isOn = status === "active" || status === "enabling";
  const busy = enable.isPending || disable.isPending || status === "enabling";

  // Window is editable independent of the toggle — "admin can extend the
  // window if they want" (S-617). Hydrated once from the persisted config so
  // a revisit shows what's actually in effect, not always the bare default.
  const [windowMonths, setWindowMonths] = useState(DEFAULT_ANALYTICS_WINDOW_MONTHS);
  const [windowHydrated, setWindowHydrated] = useState(false);
  useEffect(() => {
    if (windowHydrated || configQuery.data?.windowMonths === undefined) return;
    setWindowMonths(configQuery.data.windowMonths);
    setWindowHydrated(true);
  }, [windowHydrated, configQuery.data?.windowMonths]);

  const handleToggle = async (next: boolean): Promise<void> => {
    setPending(null);
    try {
      if (next) {
        await enable.mutateAsync({ windowMonths });
      } else {
        await disable.mutateAsync();
      }
    } catch (err) {
      setPending(getApiErrorMessage(err) ?? "Pending backend: S-617.");
    }
  };

  return (
    <section className="set__sec" data-testid="set-precedent-analytics">
      <SectionHead
        id="precedentAnalytics"
        title={
          <>
            Precedent analytics <em>· structured aggregate queries</em>
          </>
        }
        sub="Answer questions like “average compensation for [talent] across feature-film deals” with real aggregates over your precedent corpus — not just semantic search. Turning this on provisions a dedicated schema, isolated from every other studio; turning it off never deletes your data."
      />
      <div className="set__row">
        <div className="set__row__k">
          Enable precedent analytics
          <em>
            Provisions your studio's schema the first time you turn this on.
          </em>
        </div>
        <div className="set__row__v">
          <Switch
            checked={isOn}
            onCheckedChange={(next) => void handleToggle(next)}
            // Unconfigured backends can't honour a flip, so don't offer one —
            // an inert switch that silently reverts is worse than a disabled
            // one that says why.
            disabled={busy || notConfigured}
            label={notConfigured ? "Unavailable here" : statusLabel(status)}
          />
        </div>
      </div>
      {notConfigured ? (
        <div className="set__row">
          <div className="set__row__k">
            <em>
              This backend has no connection to a measures store, so precedent
              analytics can't be turned on here. That's a deployment wiring gap,
              not a setting — nothing you change on this screen will fix it.
            </em>
          </div>
        </div>
      ) : null}
      <div className="set__row">
        <div className="set__row__k">
          Analytics window
          <em>
            How far back to look — deals from before this window aren't
            included. Older comps may not reflect current terms. Default 36
            months; widen it if you want more history.
          </em>
        </div>
        <div className="set__row__v">
          <input
            type="number"
            min={1}
            step={1}
            value={windowMonths}
            onChange={(e) => setWindowMonths(Math.max(1, Number(e.target.value) || 1))}
            className="set__notif__webhook"
            style={{ maxWidth: "8rem" }}
            data-testid="set-precedent-analytics-window"
          />{" "}
          months
        </div>
      </div>
      {status === "active" && configQuery.data?.schema !== undefined && (
        <div className="set__row">
          <div className="set__row__k">Schema</div>
          <div className="set__row__v">
            <code>{configQuery.data.schema}</code>
          </div>
        </div>
      )}
      {pending !== null && (
        <div className="set__connector__notice" role="status">
          {pending}
        </div>
      )}
      {status === "failed" && configQuery.data?.lastError !== undefined && (
        <div className="set__sec__err" role="alert">
          Last attempt failed — {configQuery.data.lastError}
        </div>
      )}
      {status === "active" && (
        <PrecedentAnalyticsPopulate tenantId={tenantId} windowMonths={windowMonths} />
      )}
    </section>
  );
}

function statusLabel(status: PrecedentAnalyticsStatus): string {
  switch (status) {
    case "active":
      return "On";
    case "enabling":
      return "Enabling…";
    case "failed":
      return "Off — last attempt failed";
    case "disabled":
    default:
      return "Off";
  }
}

// [S-617] The "ask the admin if they want to populate" flow — an estimate
// (so a large corpus isn't a surprise) + a start button + polled progress.
// Always visible while analytics is active, not just right after enabling,
// so extending the window and re-populating later is the same control.
function PrecedentAnalyticsPopulate({
  tenantId,
  windowMonths,
}: {
  tenantId: string;
  windowMonths: number;
}): JSX.Element {
  const estimateQuery = useBackfillEstimate(windowMonths, true);
  const start = useStartBackfill(tenantId);
  const [polling, setPolling] = useState(false);
  const statusQuery = useBackfillStatus(tenantId, polling);
  const [startError, setStartError] = useState<string | null>(null);

  // Pick up an already-running or previously-completed run on load (e.g. a
  // revisit mid-scan, or after a page refresh) instead of only reacting to
  // this session's own "Populate now" click.
  const initialStatusQuery = useBackfillStatus(tenantId, !polling);
  useEffect(() => {
    if (initialStatusQuery.data?.status === "running") setPolling(true);
  }, [initialStatusQuery.data?.status]);

  const run = polling ? statusQuery.data : initialStatusQuery.data;
  const isRunning = run?.status === "running";

  // Re-extract is confirmed, a normal scan isn't. A normal scan now skips
  // everything already derived from an unchanged document, so its cost scales
  // with what's NEW; re-extract deliberately redoes all of it.
  const [confirmForce, setConfirmForce] = useState(false);

  const handlePopulate = async (force = false): Promise<void> => {
    setStartError(null);
    setConfirmForce(false);
    try {
      await start.mutateAsync(force ? { windowMonths, force: true } : { windowMonths });
      setPolling(true);
    } catch (err) {
      setStartError(getApiErrorMessage(err) ?? "Pending backend: S-617.");
    }
  };

  return (
    <div className="set__row" data-testid="set-precedent-analytics-populate">
      <div className="set__row__k">
        Populate precedents
        <em>
          {estimateQuery.data !== undefined
            ? `This will scan ~${estimateQuery.data.count} precedent${estimateQuery.data.count === 1 ? "" : "s"} from the last ${windowMonths} months.`
            : "Estimating how many precedents this window covers…"}
        </em>
      </div>
      <div className="set__row__v">
        {isRunning ? (
          <div role="status" data-testid="set-precedent-analytics-progress">
            Processing… {run.processed} / {run.totalCandidates || "?"}
          </div>
        ) : (
          <>
            <Button
              variant="secondary"
              className="btn--s"
              onClick={() => void handlePopulate()}
              disabled={start.isPending}
              data-testid="set-precedent-analytics-populate-btn"
            >
              {start.isPending ? "Starting…" : run !== null && run !== undefined ? "Re-scan" : "Populate now"}
            </Button>
            {/* Only offered once a run exists — with nothing stored there is
                nothing to re-extract, and the button would just be a slower
                duplicate of "Populate now". */}
            {run !== null && run !== undefined && (
              <Button
                variant="ghost"
                className="btn--s"
                onClick={() => setConfirmForce(true)}
                disabled={start.isPending}
                data-testid="set-precedent-analytics-force-btn"
              >
                Re-extract all
              </Button>
            )}
          </>
        )}
        {run?.status === "completed" && (
          <div className="set__connector__sub">
            Last run: {run.written} written,{" "}
            {/* Only shown when the backend reported it — runs recorded before
                skip-if-unchanged existed omit the field, and "0 already up to
                date" would be a claim we can't make about them. */}
            {run.skippedUnchanged !== undefined && (
              <>{run.skippedUnchanged} already up to date, </>
            )}
            {run.skippedNoSourceFile} skipped (no source file),{" "}
            {run.skippedNoCompensation} skipped (no compensation amount).
          </div>
        )}
        {run?.status === "failed" && (
          <div className="set__sec__err" role="alert">
            Scan failed — {run.error}
          </div>
        )}
        {startError !== null && (
          <div className="set__connector__notice" role="status">
            {startError}
          </div>
        )}
        <Dialog
          open={confirmForce}
          onOpenChange={setConfirmForce}
          title="Re-extract every precedent?"
          description={
            `A normal scan skips precedents already measured from the same document, because ` +
            `a signed contract doesn't change. Re-extract ignores that and re-derives all ` +
            `~${estimateQuery.data?.count ?? "?"} of them.\n\n` +
            `Only worth doing when the extraction itself has improved — a new model or parser ` +
            `would read the same document differently. Nothing is deleted: earlier values are ` +
            `kept and results use the newest.`
          }
        >
          <div className="set__row__v">
            <Button
              variant="secondary"
              className="btn--s"
              onClick={() => void handlePopulate(true)}
              disabled={start.isPending}
              data-testid="set-precedent-analytics-force-confirm"
            >
              Re-extract all
            </Button>
            <Button
              variant="ghost"
              className="btn--s"
              onClick={() => setConfirmForce(false)}
              data-testid="set-precedent-analytics-force-cancel"
            >
              Cancel
            </Button>
          </div>
        </Dialog>
      </div>
    </div>
  );
}

// [S-240] Look & feel — real tenant branding. Reads
// useTenantPreferences (returns defaults when no doc exists yet),
// writes via PUT /tenants/:id/preferences/branding. Primary color
// applies live via a CSS variable hooked onto <html> (see
// applyBrandingToDocument).
function LookFeelSection({ tenantId }: { tenantId: string }): JSX.Element {
  const prefsQuery = useTenantPreferences(tenantId);
  const updateBranding = useUpdateBranding();

  const stored = prefsQuery.data?.branding ?? null;
  const [primaryColor, setPrimaryColor] = useState<BrandingPrimaryColor>("blue");
  const [darkMode, setDarkMode] = useState<boolean>(false);

  useEffect(() => {
    if (stored !== null) {
      setPrimaryColor(stored.primaryColor);
      setDarkMode(stored.darkMode);
      applyBrandingToDocument(stored.primaryColor, stored.darkMode);
    }
  }, [stored?.primaryColor, stored?.darkMode]);

  const dirty =
    stored !== null &&
    (primaryColor !== stored.primaryColor || darkMode !== stored.darkMode);
  const canSave = dirty && !updateBranding.isPending && tenantId.length > 0;

  const handleSave = (): void => {
    if (!canSave) return;
    // Local preview while the round-trip is in flight — the
    // useEffect above will re-confirm on success.
    applyBrandingToDocument(primaryColor, darkMode);
    updateBranding.mutate({
      tenantId,
      branding: { primaryColor, darkMode },
    });
  };

  return (
    <section className="set__sec" data-testid="set-look">
      <SectionHead
        id="look"
        title="Look & feel"
        sub="Branding applied to the web app for this organization. Saves apply immediately to your session."
        action={
          tenantId.length === 0 ? (
            <Button variant="primary" className="btn--s" disabled>
              Save
            </Button>
          ) : (
            <Button
              variant="primary"
              className="btn--s"
              onClick={handleSave}
              disabled={!canSave}
              data-testid="set-look-save"
            >
              {updateBranding.isPending ? "Saving…" : "Save"}
            </Button>
          )
        }
      />
      <div className="set__row">
        <div className="set__row__k">
          Accent color
          <em>Used for primary buttons and active states.</em>
        </div>
        <div className="set__row__v">
          <div className="set__lf__swatches">
            {BRANDING_COLORS.map((name) => (
              <Button
                key={name}
                variant="bare"
                aria-label={`accent ${name}`}
                aria-pressed={primaryColor === name}
                className={`set__lf__swatch set__lf__swatch--${name}${
                  primaryColor === name ? " is-on" : ""
                }`}
                onClick={() => setPrimaryColor(name)}
                data-testid={`set-look-swatch-${name}`}
              >
                <span className="set__lf__swatch__label">{name}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>
      <div className="set__row">
        <div className="set__row__k">
          Theme
          <em>Plugin and web app default.</em>
        </div>
        <div className="set__row__v">
          <Checkbox
            checked={darkMode}
            onCheckedChange={(v) => setDarkMode(v === true)}
            label="Allow dark mode"
          />
        </div>
      </div>
      {updateBranding.isError && (
        <div className="set__sec__err" role="alert">
          Save failed — {updateBranding.error?.message ?? "unknown error"}
        </div>
      )}
    </section>
  );
}

// Boot-time + per-change hook that paints the chosen accent + theme.
// Stays a plain function (not a useEffect) so callers can run it on
// app boot and on every successful save.
export function applyBrandingToDocument(
  color: BrandingPrimaryColor,
  darkMode: boolean
): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-accent", color);
  document.documentElement.setAttribute(
    "data-theme",
    darkMode ? "dark" : "light"
  );
}

// S-095c — Security policy section. Sentinel "none" maps to the
// nullable ssoProvider; the Select primitive bans null values, so we
// translate at the boundary rather than threading null through the
// component.
const SSO_NONE = "none" as const;
type SsoSelectValue = SsoProvider | typeof SSO_NONE;

const SSO_OPTIONS: ReadonlyArray<{
  value: SsoSelectValue;
  label: string;
}> = [
  { value: SSO_NONE, label: "Not configured" },
  { value: "okta", label: "Okta (SAML)" },
  { value: "azure", label: "Azure AD (OIDC)" },
  { value: "google", label: "Google Workspace" },
];

const RETENTION_OPTIONS: ReadonlyArray<{
  value: `${AuditRetentionDays}`;
  label: string;
}> = [
  { value: "2555", label: "7 years (legal)" },
  { value: "1095", label: "3 years" },
  { value: "365", label: "1 year" },
];

function SecuritySection({
  tenantId,
  policy,
  isLoading,
  isError,
}: {
  tenantId: string;
  policy: SecurityPolicyRow | null;
  isLoading: boolean;
  isError: boolean;
}): JSX.Element {
  // Local form state — seeded from the live policy on first render
  // (or default values until it arrives). Save fires a single PUT
  // with the full shape.
  const [sso, setSso] = useState<SsoSelectValue>(SSO_NONE);
  const [mfa, setMfa] = useState(true);
  const [retention, setRetention] =
    useState<`${AuditRetentionDays}`>("2555");
  const mutation = useUpdateSecurityPolicy(tenantId);

  useEffect(() => {
    if (policy === null) return;
    setSso(policy.ssoProvider ?? SSO_NONE);
    setMfa(policy.mfaRequired);
    setRetention(`${policy.auditRetentionDays}`);
  }, [policy]);

  const dirty =
    policy !== null &&
    ((policy.ssoProvider ?? SSO_NONE) !== sso ||
      policy.mfaRequired !== mfa ||
      `${policy.auditRetentionDays}` !== retention);
  const canSave =
    dirty && tenantId.length > 0 && !isLoading && !isError && !mutation.isPending;

  const onSave = (): void => {
    mutation.mutate({
      ssoProvider: sso === SSO_NONE ? null : sso,
      mfaRequired: mfa,
      auditRetentionDays: Number(retention) as AuditRetentionDays,
    });
  };

  return (
    <section className="set__sec" data-testid="set-security">
      <SectionHead
        id="security"
        title="Security"
        sub="SSO provider, MFA policy, and audit retention. Saved changes write a role-change audit event."
        action={
          <Button
            variant="primary"
            className="btn--s"
            disabled={!canSave}
            onClick={onSave}
            data-testid="set-security-save"
          >
            {mutation.isPending ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        }
      />
      {isError && (
        <Alert
          label="Couldn't load the security policy"
          tone="error"
          data-testid="set-security-error"
        >
          Try refreshing.
        </Alert>
      )}
      <div className="set__row">
        <div className="set__row__k">
          SSO provider
          <em>SAML 2.0 or OIDC.</em>
        </div>
        <div className="set__row__v">
          <Select
            label="SSO provider"
            value={sso}
            onValueChange={(v) => {
              if (v !== undefined) setSso(v);
            }}
            disabled={isLoading || isError}
            clearable={false}
            options={SSO_OPTIONS}
          />
        </div>
      </div>
      <div className="set__row">
        <div className="set__row__k">
          MFA
          <em>Required when SSO is bypassed.</em>
        </div>
        <div className="set__row__v">
          <Checkbox
            checked={mfa}
            onCheckedChange={(c) => setMfa(c === true)}
            disabled={isLoading || isError}
            label="Required"
            id="set-security-mfa"
          />
        </div>
      </div>
      <div className="set__row">
        <div className="set__row__k">
          Audit retention
          <em>How long to keep activity events.</em>
        </div>
        <div className="set__row__v">
          <Select
            label="Audit retention"
            value={retention}
            onValueChange={(v) => {
              if (v !== undefined) setRetention(v);
            }}
            disabled={isLoading || isError}
            clearable={false}
            options={RETENTION_OPTIONS}
          />
        </div>
      </div>
      {mutation.isError && (
        <div
          className="tnt__form__error"
          role="alert"
          data-testid="set-security-save-error"
        >
          Could not save the policy. Check your inputs and try again.
        </div>
      )}
    </section>
  );
}

// [S-314] Friendly labels for the capability catalog — the raw
// `domain:action` ids are accurate but dev-facing. Keyed exhaustively over
// Capability so a newly-added capability fails typecheck until it's labelled.
const CAPABILITY_LABELS: Record<Capability, string> = {
  "template:author": "Author templates",
  "template:tag": "Tag templates",
  "template:certify": "Certify templates",
  "platform:manage_tenants": "Manage tenants",
  "tenant:manage": "Manage tenant config",
  "tenant:read": "Read tenant config",
  "classification:manage": "Manage classification",
  "reattestation:manage": "Manage re-attestation",
  "ingestion:submit": "Submit documents",
  "ingestion:operate": "Retry failed/stalled uploads",
  "contract:access": "Access contracts",
  "review:moderate": "Moderate review",
  "attestation:access": "Access attestations",
  "exclusion:manage": "Manage exclusions",
  "rbac:manage": "Manage roles & permissions",
  "analytics:view_tenant": "View tenant analytics",
  "analytics:view_platform": "View platform analytics",
  "precedent:business_affairs": "See business-affairs precedents",
  "agent:manage": "Manage persona configuration",
  "marketplace:submit_listing": "Submit marketplace listings",
  "marketplace:manage_own_listings": "Manage own marketplace listings",
  // [S-723] The platform-side moderation capability — added with the
  // submit/approve/reject flow; this map is exhaustive over Capability, so
  // typecheck had been failing on main until it was labelled here.
  "marketplace:moderate_listings": "Moderate marketplace listings",
};

const BUILTIN_ROLE_LABELS: Record<Role, string> = {
  platform_admin: "Platform admin",
  tenant_admin: "Organization admin",
  tenant_user: "Member",
  contributor: "Contributor",
};

// Mirrors CustomRoleDefinitionSchema's name rule (lowercase, starts with a
// letter, snake_case). Validated client-side for UX; the server re-validates.
const ROLE_NAME_RE = /^[a-z][a-z0-9_]*$/;

function CapDot({ on }: { on: boolean }): JSX.Element {
  return (
    <span
      aria-label={on ? "granted" : "not granted"}
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: 999,
        background: on ? "var(--cs-certified)" : "var(--cs-text-tertiary)",
        opacity: on ? 1 : 0.4,
      }}
    />
  );
}

function RbacSection({
  tenantId,
  users,
}: {
  tenantId: string;
  users: TenantUserRow[] | null;
}): JSX.Element {
  // [S-314] Real RBAC management — replaces the S-241 reference mock. Lists +
  // defines + deletes tenant custom roles and assigns them to members, all
  // through the S-313 admin routes (gated on rbac:manage server-side). The
  // built-in matrix at the top is the live ROLE_CAPABILITIES default.
  // Capabilities the caller can grant in a custom role: their OWN effective set
  // (privilege-delegation containment — an admin can't grant a capability they
  // don't hold), plus precedent:business_affairs so an admin can DELEGATE the BA
  // tier via a custom role even though it isn't in the tenant_admin DEFAULT set.
  const session = useSession();
  const grantable = Array.from(
    new Set<Capability>([
      ...(session !== null ? effectiveCapabilities(session) : []),
      "precedent:business_affairs",
    ])
  );

  const rolesQuery = useCustomRoles(tenantId);
  const defineRole = useDefineCustomRole();
  const deleteRole = useDeleteCustomRole();
  const assignRole = useAssignCustomRole();
  const clearRole = useClearAssignedRole();

  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [caps, setCaps] = useState<ReadonlySet<Capability>>(new Set());
  const [assignUid, setAssignUid] = useState<string | undefined>(undefined);
  const [assignRoleName, setAssignRoleName] = useState<string | undefined>(
    undefined
  );

  const roles = rolesQuery.data ?? [];
  const activeUsers = users?.filter((u) => u.deactivatedAt === null) ?? [];
  const nameValid = name.length === 0 || ROLE_NAME_RE.test(name);
  const canCreate =
    tenantId.length > 0 &&
    ROLE_NAME_RE.test(name) &&
    displayName.trim().length > 0 &&
    !defineRole.isPending;
  const canAssign =
    tenantId.length > 0 &&
    assignUid !== undefined &&
    assignRoleName !== undefined &&
    !assignRole.isPending &&
    !clearRole.isPending;

  const toggleCap = (c: Capability): void =>
    setCaps((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  const onCreate = (): void =>
    defineRole.mutate(
      {
        tenantId,
        name,
        displayName: displayName.trim(),
        capabilities: [...caps],
      },
      {
        onSuccess: () => {
          setName("");
          setDisplayName("");
          setCaps(new Set());
        },
      }
    );

  return (
    <section className="set__sec" data-testid="set-rbac">
      <SectionHead
        id="rbac"
        title="Roles & permissions"
        sub="Built-in roles below are the platform default. Define tenant custom roles to grant a precise capability set, then assign them to members — the grant takes effect on the member's next sign-in."
      />

      <div className="set__row">
        <div className="set__row__k">
          Built-in roles
          <em>The default capabilities each platform role carries.</em>
        </div>
        <div className="set__row__v" style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
            data-testid="set-rbac-builtin"
          >
            <thead>
              <tr style={{ textAlign: "left", color: "var(--cs-text-tertiary)" }}>
                <th style={{ padding: "8px 0", fontWeight: 500 }}>Capability</th>
                {ROLES.map((r) => (
                  <th
                    key={r}
                    style={{ padding: "8px 0", fontWeight: 500, textAlign: "center" }}
                  >
                    {BUILTIN_ROLE_LABELS[r]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAPABILITIES.map((cap) => (
                <tr key={cap} style={{ borderTop: "1px solid var(--cs-border)" }}>
                  <td
                    style={{
                      padding: "8px 0",
                      color: "var(--cs-text-primary)",
                    }}
                  >
                    {CAPABILITY_LABELS[cap]}
                  </td>
                  {ROLES.map((r) => (
                    <td key={r} style={{ padding: "8px 0", textAlign: "center" }}>
                      <CapDot on={ROLE_CAPABILITIES[r].includes(cap)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="set__row">
        <div className="set__row__k">
          Custom roles
          <em>Organization-defined personas. Assigned grants override the role default.</em>
        </div>
        <div className="set__row__v">
          {rolesQuery.isError && (
            <Alert
              label="Couldn't load custom roles"
              tone="error"
              data-testid="set-rbac-list-error"
            >
              Try refreshing.
            </Alert>
          )}
          {!rolesQuery.isError && roles.length === 0 && (
            <div style={{ color: "var(--cs-text-secondary)", fontSize: 13 }} data-testid="set-rbac-empty">
              No custom roles yet. Define one below.
            </div>
          )}
          {roles.map((r) => (
            <div
              key={r.name}
              data-testid={`set-rbac-role-${r.name}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "8px 0",
                borderTop: "1px solid var(--cs-border)",
              }}
            >
              <div>
                <div style={{ color: "var(--cs-text-primary)", fontWeight: 500 }}>
                  {r.displayName}{" "}
                  <span style={{ color: "var(--cs-text-tertiary)", fontWeight: 400 }}>
                    ({r.name})
                  </span>
                </div>
                <div style={{ color: "var(--cs-text-secondary)", fontSize: 12 }}>
                  {r.capabilities.length === 0
                    ? "No capabilities"
                    : r.capabilities
                        .map((c) => CAPABILITY_LABELS[c] ?? c)
                        .join(" · ")}
                </div>
              </div>
              <Button
                variant="ghost"
                className="btn--s"
                disabled={deleteRole.isPending}
                onClick={() => deleteRole.mutate({ tenantId, name: r.name })}
                data-testid={`set-rbac-delete-${r.name}`}
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="set__row">
        <div className="set__row__k">
          Define a role
          <em>Lowercase id (snake_case), a display name, and its capabilities.</em>
        </div>
        <div className="set__row__v">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "var(--cs-text-secondary)" }}>
                Role ID{" "}
                <span style={{ color: "var(--cs-text-tertiary)", fontWeight: 400 }}>
                  · internal id, lowercase, can’t change later
                </span>
              </span>
              <input
                className="set__inp"
                placeholder="e.g. legal_ops"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="set-rbac-name"
                aria-label="Role id"
              />
            </label>
            {!nameValid && (
              <div style={{ color: "var(--cs-flagged)", fontSize: 12 }} data-testid="set-rbac-name-hint">
                Use lowercase letters, digits, and underscores; start with a letter.
              </div>
            )}
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "var(--cs-text-secondary)" }}>
                Display name{" "}
                <span style={{ color: "var(--cs-text-tertiary)", fontWeight: 400 }}>
                  · the friendly name members see
                </span>
              </span>
              <input
                className="set__inp"
                placeholder="e.g. Legal Operations"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                data-testid="set-rbac-display"
                aria-label="Role display name"
              />
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 6,
              }}
            >
              {grantable.map((c) => (
                <Checkbox
                  key={c}
                  id={`set-rbac-cap-${c}`}
                  data-testid={`set-rbac-cap-${c}`}
                  checked={caps.has(c)}
                  onCheckedChange={() => toggleCap(c)}
                  label={CAPABILITY_LABELS[c]}
                />
              ))}
            </div>
            <div>
              <Button
                variant="primary"
                className="btn--s"
                disabled={!canCreate}
                onClick={onCreate}
                data-testid="set-rbac-create"
              >
                {defineRole.isPending ? "Creating…" : "Create role"}
              </Button>
            </div>
            {defineRole.isError && (
              <div className="tnt__form__error" role="alert" data-testid="set-rbac-create-error">
                {getApiErrorMessage(defineRole.error) ??
                  "Could not create the role. Check the id and try again."}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="set__row">
        <div className="set__row__k">
          Assign a role
          <em>Grant a custom role to a member, or reset them to their default.</em>
        </div>
        <div className="set__row__v">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Select
              label="Member"
              value={assignUid}
              onValueChange={setAssignUid}
              clearable={false}
              disabled={activeUsers.length === 0}
              options={activeUsers.map((u) => ({
                value: u.uid,
                label: u.displayName ?? u.email,
              }))}
            />
            <Select
              label="Custom role"
              value={assignRoleName}
              onValueChange={setAssignRoleName}
              clearable={false}
              disabled={roles.length === 0}
              options={roles.map((r) => ({
                value: r.name,
                label: r.displayName,
              }))}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                variant="primary"
                className="btn--s"
                disabled={!canAssign}
                onClick={() => {
                  if (assignUid !== undefined && assignRoleName !== undefined) {
                    assignRole.mutate({
                      tenantId,
                      uid: assignUid,
                      roleName: assignRoleName,
                    });
                  }
                }}
                data-testid="set-rbac-assign"
              >
                {assignRole.isPending ? "Assigning…" : "Assign"}
              </Button>
              <Button
                variant="ghost"
                className="btn--s"
                disabled={
                  tenantId.length === 0 ||
                  assignUid === undefined ||
                  clearRole.isPending ||
                  assignRole.isPending
                }
                onClick={() => {
                  if (assignUid !== undefined) {
                    clearRole.mutate({ tenantId, uid: assignUid });
                  }
                }}
                data-testid="set-rbac-clear"
              >
                {clearRole.isPending ? "Resetting…" : "Reset to default"}
              </Button>
            </div>
            {assignRole.isSuccess && (
              <div style={{ color: "var(--cs-certified)", fontSize: 12 }} data-testid="set-rbac-assign-ok">
                Assigned. Takes effect on the member's next sign-in.
              </div>
            )}
            {(assignRole.isError || clearRole.isError) && (
              <div className="tnt__form__error" role="alert" data-testid="set-rbac-assign-error">
                {getApiErrorMessage(assignRole.error ?? clearRole.error) ??
                  "Could not update the assignment. Try again."}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function MembersSection({
  tenantId,
  users,
  isLoading,
  isFetching,
  isError,
}: {
  tenantId: string;
  users: TenantUserRow[] | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}): JSX.Element {
  // S-095e — list-only on this surface. Invite / role-change / remove
  // already ship at the dedicated /admin/tenants/:tenantId/users
  // surface (S-091); the "+ Invite member" action here deep-links there
  // rather than duplicating the modal stack in the Settings rail. The
  // separate-surface decision is documented in the file header and the
  // S-095e PR description.
  const activeUsers = users?.filter((u) => u.deactivatedAt === null) ?? [];
  // The role shown here is the user's membership role *within this organization*
  // (the Firestore user doc), not their platform-wide claim. A
  // platform_admin can appear in an organization's roster as a tenant_admin — so
  // mark the viewer's own row to keep that from reading as a contradiction
  // against the sidebar's platform role.
  const session = useSession();
  return (
    <section className="set__sec" data-testid="set-members">
      <SectionHead
        id="members"
        title={
          <>
            Members{" "}
            <em>
              · {users === null ? "—" : `${activeUsers.length} active`}
            </em>
          </>
        }
        sub="Members of this organization. Provisioning, role changes, and removal happen on the dedicated Users surface."
        action={
          tenantId.length === 0 ? (
            <Button variant="primary" className="btn--s" disabled>
              + Invite member
            </Button>
          ) : (
            <Link
              to={`/admin/tenants/${encodeURIComponent(tenantId)}/users`}
              className="btn btn--p btn--s"
              data-testid="set-members-invite-link"
            >
              + Manage members
            </Link>
          )
        }
      />
      {isError ? (
        <div className="set__mem" data-testid="set-members-error">
          <div className="set__mem__name">
            Members unavailable
            <em>The admin API responded with an error.</em>
          </div>
        </div>
      ) : isLoading ||
        users === null ||
        (isFetching && (users?.length ?? 0) === 0) ? (
        <div className="set__mem" data-testid="set-members-loading">
          <div className="set__mem__name"><Spinner size={13} /> Loading members…</div>
        </div>
      ) : !isFetching && users.length === 0 ? (
        <div className="set__mem" data-testid="set-members-empty">
          <div className="set__mem__name">
            No members yet
            <em>
              Invite the first team member from the Users surface.
            </em>
          </div>
        </div>
      ) : (
        users.map((u) => (
          <div
            className="set__mem"
            key={u.uid}
            data-testid={`set-members-row-${u.uid}`}
          >
            <div className="set__mem__av">{initialsForEmail(u.email)}</div>
            <div className="set__mem__name">
              {u.email}
              {u.uid === session?.uid && (
                <span className="set__mem__you">you</span>
              )}
              {u.deactivatedAt !== null && (
                <em>deactivated · audit log preserved</em>
              )}
            </div>
            <div className="set__mem__role" title="Role within this organization">
              {u.role}
            </div>
            <div className="set__mem__last">
              joined {formatShortDate(u.createdAt)}
            </div>
          </div>
        ))
      )}
    </section>
  );
}

function initialsForEmail(email: string): string {
  // Take the local part of the email, split on dots/underscores,
  // build a 2-letter monogram. Falls back to the first two characters
  // when the local part is short.
  const local = email.split("@")[0] ?? "";
  const tokens = local.split(/[._-]+/).filter((t) => t.length > 0);
  if (tokens.length >= 2) {
    return (tokens[0]![0]! + tokens[1]![0]!).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function SaveBar(): JSX.Element {
  // [S-245] Each Settings section that has a backend (General time zone,
  // Security policy, API keys) saves inline with its own buttons. No
  // global Save Changes flow remains — the previous "Backend wiring
  // pending" banner was a scaffold from the preview era. Surfacing a
  // calm status line here is friendlier than removing the bar entirely
  // (which would shift focus).
  return (
    <div className="set__savebar" data-testid="set-savebar">
      <span className="set__savebar__txt">
        Each section saves on its own. Audit events fire on every write,
        scoped to your organization.
      </span>
      <Link to="/activity" className="btn btn--g btn--s">
        View audit activity →
      </Link>
    </div>
  );
}

function prettyStudio(studioId: string | null | undefined): string {
  if (studioId === undefined || studioId === null) return "ContractStudio";
  return studioId[0]!.toUpperCase() + studioId.slice(1);
}

// The Settings header showed the viewer's role as a flat "Tenant admin"
// label regardless of who was looking — misleading for a platform_admin
// (who reaches this screen via the route's minRole=tenant_admin gate).
// Mirror the sidebar's prettyRole so the header tells the truth about the
// caller. tenant_user can't reach Settings, so it's listed only for
// completeness.
function prettyRole(role: Role | undefined): string {
  switch (role) {
    case "platform_admin":
      return "Platform admin";
    case "tenant_admin":
      return "Organization admin";
    case "tenant_user":
      return "Attorney";
    default:
      return "—";
  }
}

// ────────────────────────────────────────────────────────────────────────
// [S-204] API Keys section — issue / list / revoke tenant API keys.
// Backed by POST/GET/DELETE /tenants/:id/api-keys. The plaintext secret
// comes back ONCE on issue; this UI surfaces it in a one-time copy
// affordance and never persists it.
// ────────────────────────────────────────────────────────────────────────

function ApiKeysSection({ tenantId }: { tenantId: string }): JSX.Element {
  const keysQuery = useApiKeys(tenantId);
  const issue = useIssueApiKey();
  const revoke = useRevokeApiKey();
  const [newName, setNewName] = useState("");
  const [issuedSecret, setIssuedSecret] = useState<
    { keyId: string; name: string; secret: string } | null
  >(null);
  const [copied, setCopied] = useState(false);

  const submit = (): void => {
    const trimmed = newName.trim();
    if (trimmed.length === 0 || tenantId.length === 0 || issue.isPending) return;
    issue.mutate(
      { tenantId, name: trimmed },
      {
        onSuccess: (out) => {
          setIssuedSecret({
            keyId: out.record.id,
            name: out.record.name,
            secret: out.secret,
          });
          setNewName("");
          setCopied(false);
        },
      }
    );
  };

  const dismissSecret = (): void => {
    setIssuedSecret(null);
    setCopied(false);
  };

  const copySecret = async (): Promise<void> => {
    if (issuedSecret === null) return;
    try {
      await navigator.clipboard.writeText(issuedSecret.secret);
      setCopied(true);
    } catch {
      // Clipboard may be unavailable (e.g. http://localhost in some browsers);
      // the inline text remains visible so the user can copy manually.
    }
  };

  const onRevoke = (keyId: string): void => {
    if (tenantId.length === 0 || revoke.isPending) return;
    revoke.mutate({ tenantId, keyId });
  };

  const allKeys = keysQuery.data ?? [];
  const activeKeys = allKeys.filter((k) => k.revokedAt === null);
  const revokedKeys = allKeys.filter((k) => k.revokedAt !== null);

  return (
    <section className="set__sec" data-testid="set-apikeys">
      <SectionHead
        id="apikeys"
        title={
          <>
            API keys{" "}
            <em>
              ·{" "}
              {keysQuery.isLoading
                ? "—"
                : `${activeKeys.length} active${
                    revokedKeys.length > 0
                      ? `, ${revokedKeys.length} revoked`
                      : ""
                  }`}
            </em>
          </>
        }
        sub="Server-to-server credentials scoped to this organization. Issued secrets are shown ONCE — store them in a vault immediately."
      />

      {tenantId.length === 0 ? (
        <div className="set__apik__empty" data-testid="set-apikeys-no-tenant">
          Sign in as an organization admin to manage API keys.
        </div>
      ) : (
        <>
          <div className="set__apik__form">
            <input
              type="text"
              placeholder='Key name (e.g. "MCP server — staging")'
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={120}
              disabled={issue.isPending}
              className="set__apik__name"
              data-testid="set-apikeys-name"
            />
            <Button
              variant="primary"
              className="btn--s"
              onClick={submit}
              disabled={
                issue.isPending ||
                newName.trim().length === 0 ||
                issuedSecret !== null
              }
              data-testid="set-apikeys-issue"
            >
              {issue.isPending ? "Issuing…" : "+ Issue key"}
            </Button>
          </div>

          {issue.isError && (
            <Alert label="Couldn't issue key" tone="error">
              {issue.error?.message ?? "unknown error"}
            </Alert>
          )}

          {issuedSecret !== null && (
            <div
              className="set__apik__secret"
              role="status"
              aria-live="polite"
              data-testid="set-apikeys-secret"
            >
              <div className="set__apik__secret__hdr">
                <strong>Key issued</strong>
                <span>· {issuedSecret.name}</span>
              </div>
              <div className="set__apik__secret__warn">
                This secret is shown ONCE. Copy it now — there is no way to
                recover it after you dismiss this banner.
              </div>
              <div className="set__apik__secret__row">
                <code className="set__apik__secret__val">
                  {issuedSecret.secret}
                </code>
                <Button
                  variant="secondary"
                  className="btn--s"
                  onClick={() => void copySecret()}
                  data-testid="set-apikeys-copy"
                >
                  {copied ? "Copied ✓" : "Copy"}
                </Button>
                <Button
                  variant="ghost"
                  className="btn--s"
                  onClick={dismissSecret}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          {keysQuery.isLoading ? (
            <div className="set__apik__empty" data-testid="set-apikeys-loading">
              Loading keys…
            </div>
          ) : keysQuery.isError ? (
            <Alert label="Couldn't load keys" tone="error">
              {keysQuery.error?.message ?? "unknown error"}
            </Alert>
          ) : allKeys.length === 0 ? (
            <div className="set__apik__empty" data-testid="set-apikeys-empty">
              No API keys yet. Issue one above to enable MCP server / Layer 2
              integrations.
            </div>
          ) : (
            <ul className="set__apik__list">
              {[...activeKeys, ...revokedKeys].map((k) => (
                <ApiKeyRow
                  key={k.id}
                  record={k}
                  onRevoke={() => onRevoke(k.id)}
                  revoking={revoke.isPending && revoke.variables?.keyId === k.id}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

// [S-058] MCP access — endpoint, live status, the tools unlocked at this
// tenant's tier, and copy-paste instructions for connecting Claude (or any
// MCP client). Tools + tier + status come from the admin backend
// (/tenants/me/mcp-access); nothing here is hardcoded sample data. When the
// MCP server isn't yet deployed in this environment, endpoint is null and
// status is reported honestly as "unknown".
// Build the full Claude Code connect command. When we have a real key the
// command is copy-paste ready; before minting one we show the same shape with
// a key placeholder so the structure is visible.
function buildConnectCmd(endpoint: string, secret: string): string {
  return (
    `claude mcp add --transport http contractstudio \\\n` +
    `  ${endpoint} \\\n` +
    `  --header "Authorization: Bearer ${secret}"`
  );
}

function McpAccessSection({ tenantId }: { tenantId: string }): JSX.Element {
  const accessQuery = useMcpAccess(tenantId);
  const access = accessQuery.data ?? null;
  const deployQuery = useMcpDeployment(tenantId);
  const deploy = useDeployMcp();
  const undeploy = useUndeployMcp();
  const issue = useIssueApiKey();
  const [copied, setCopied] = useState<"endpoint" | "cmd" | null>(null);
  // The complete command with a freshly-minted key, shown once after the user
  // clicks "Generate". Holds the plaintext secret — never persisted or refetched.
  const [issued, setIssued] = useState<{ secret: string; cmd: string } | null>(
    null
  );

  const copy = async (what: "endpoint" | "cmd", text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch {
      // Clipboard may be unavailable; the inline text stays selectable.
    }
  };

  // Prefer this organization's own deployed service URL; fall back to the shared
  // endpoint (MCP_PUBLIC_URL) or a placeholder.
  const dep = deployQuery.data?.deployment ?? null;
  const tenantEndpoint =
    dep?.status === "deployed" && dep.endpointUrl !== null
      ? `${dep.endpointUrl.replace(/\/+$/, "")}/mcp`
      : null;
  const effectiveEndpoint = tenantEndpoint ?? access?.endpointUrl ?? null;
  const endpoint = effectiveEndpoint ?? "<your-mcp-endpoint>";
  // Default (no key minted yet) shows the shape with a placeholder; once a key
  // is generated we swap in the fully-filled, copy-paste-ready command.
  const connectCmd =
    issued?.cmd ?? buildConnectCmd(endpoint, "csai_<your-api-key>");

  // One-click connect: mint a scoped key and fill the command with the real
  // endpoint + secret. A secret can't be recovered later, so producing a
  // complete command necessarily issues a fresh key.
  const generate = (): void => {
    if (
      effectiveEndpoint === null ||
      tenantId.length === 0 ||
      issue.isPending
    ) {
      return;
    }
    const name = `Claude MCP — ${new Date().toISOString().slice(0, 10)}`;
    issue.mutate(
      { tenantId, name },
      {
        onSuccess: (out) => {
          setIssued({
            secret: out.secret,
            cmd: buildConnectCmd(effectiveEndpoint, out.secret),
          });
          setCopied(null);
        },
      }
    );
  };

  // Status reflects connectability: a usable endpoint reads as online even when
  // a *dedicated* server hasn't been provisioned. "not deployed" is reserved
  // for the genuine no-endpoint case.
  const depStatus = dep?.status;
  const effectiveStatusLabel =
    depStatus === "deploying"
      ? "deploying…"
      : depStatus === "error"
        ? "deploy failed"
        : effectiveEndpoint === null
          ? "not deployed"
          : depStatus === "deployed"
            ? "deployed"
            : access?.status === "online"
              ? "online"
              : access?.status === "offline"
                ? "offline"
                : "unknown";
  const effectiveStatusClass =
    depStatus === "error" || access?.status === "offline"
      ? "offline"
      : depStatus === "deploying" || effectiveEndpoint === null
        ? "unknown"
        : depStatus === "deployed" || access?.status === "online"
          ? "online"
          : "unknown";

  return (
    <section className="set__sec" data-testid="set-mcp">
      <SectionHead
        id="mcp"
        title="MCP access"
        sub="Connect Claude — or any MCP client — to your organization's ContractStudio tools. Tools shown are scoped to your access tier."
      />

      {tenantId.length === 0 ? (
        <div className="set__mcp__empty" data-testid="set-mcp-no-tenant">
          Sign in as an organization admin to see MCP connection details.
        </div>
      ) : accessQuery.isLoading ? (
        <div className="set__mcp__empty" data-testid="set-mcp-loading">
          Loading MCP details…
        </div>
      ) : accessQuery.isError || access === null ? (
        <Alert label="Couldn't load MCP details" tone="error">
          {accessQuery.error?.message ?? "unknown error"}
        </Alert>
      ) : (
        <>
          <div className="set__mcp__meta">
            <div className="set__mcp__field">
              <span className="set__mcp__lbl">Endpoint</span>
              {effectiveEndpoint !== null ? (
                <span className="set__mcp__eprow">
                  <code className="set__mcp__ep" data-testid="set-mcp-endpoint">
                    {effectiveEndpoint}
                  </code>
                  <Button
                    variant="secondary"
                    className="btn--s"
                    onClick={() => void copy("endpoint", effectiveEndpoint)}
                    data-testid="set-mcp-copy-endpoint"
                  >
                    {copied === "endpoint" ? "Copied ✓" : "Copy"}
                  </Button>
                </span>
              ) : (
                <span
                  className="set__mcp__ep set__mcp__ep--none"
                  data-testid="set-mcp-endpoint"
                >
                  Deploy your organization's MCP server below to get an endpoint.
                </span>
              )}
            </div>

            <div className="set__mcp__field">
              <span className="set__mcp__lbl">Status</span>
              <span
                className={`set__mcp__status is-${effectiveStatusClass}`}
                data-testid="set-mcp-status"
              >
                <span className="dot" /> {effectiveStatusLabel}
              </span>
            </div>
          </div>

          <div className="set__mcp__tools" data-testid="set-mcp-tools">
            <div className="set__mcp__lbl">
              Tools available · {(access.tools ?? []).length} on the{" "}
              <strong>{access.accessTier}</strong> tier
            </div>
            <ul className="set__mcp__toollist">
              {(access.tools ?? []).map((t) => (
                <li
                  key={t.name}
                  className="set__mcp__tool"
                  data-testid="set-mcp-tool"
                >
                  <code className="set__mcp__toolname">{t.name}</code>
                  <span className="set__mcp__tooldesc">{t.description}</span>
                </li>
              ))}
            </ul>
          </div>

          <McpDeploymentBlock
            available={deployQuery.data?.available ?? false}
            loading={deployQuery.isLoading}
            deployment={dep}
            onDeploy={() => deploy.mutate()}
            onUndeploy={() => undeploy.mutate()}
            deploying={deploy.isPending || dep?.status === "deploying"}
            undeploying={undeploy.isPending}
            actionError={deploy.error ?? undeploy.error}
          />

          <div className="set__mcp__connect">
            <div className="set__mcp__lbl">Connect Claude</div>

            {effectiveEndpoint === null ? (
              <p className="set__mcp__note" data-testid="set-mcp-no-endpoint">
                There's no MCP server to connect to yet. Deploy your organization's
                dedicated server above — then this will give you a one-click,
                copy-paste connection command.
              </p>
            ) : (
              <>
                <p className="set__mcp__note">
                  Click below to mint a scoped API key and get the complete{" "}
                  command — endpoint and key already filled in. Paste it into
                  Claude Code, or use the same URL + header to add a custom
                  connector in Claude Desktop / claude.ai.
                </p>
                <div className="set__mcp__connectbtn">
                  <Button
                    variant="primary"
                    onClick={generate}
                    disabled={issue.isPending}
                    data-testid="set-mcp-generate"
                  >
                    {issue.isPending
                      ? "Generating…"
                      : issued !== null
                        ? "Regenerate with a new key"
                        : "Generate connection command"}
                  </Button>
                </div>

                {issue.isError && (
                  <Alert label="Couldn't generate the command" tone="error">
                    {issue.error?.message ?? "unknown error"}
                  </Alert>
                )}

                {issued !== null && (
                  <div className="set__mcp__secretwarn" role="status">
                    This command contains an API key shown <strong>once</strong>
                    . Copy it now — the secret can't be recovered after you leave
                    this page. Manage or revoke it under{" "}
                    <a href="#apikeys" className="set__mcp__link">
                      API keys
                    </a>
                    .
                  </div>
                )}

                <div className="set__mcp__cmdrow">
                  <pre className="set__mcp__cmd" data-testid="set-mcp-cmd">
                    {connectCmd}
                  </pre>
                  <Button
                    variant="secondary"
                    className="btn--s"
                    onClick={() => void copy("cmd", connectCmd)}
                    disabled={issued === null}
                    data-testid="set-mcp-copy-cmd"
                  >
                    {copied === "cmd" ? "Copied ✓" : "Copy"}
                  </Button>
                </div>
                {issued === null && (
                  <p className="set__mcp__note">
                    Above is the shape of the command — generate one to fill in
                    your key. The transport is streamable HTTP; any MCP-compatible
                    client can connect with the endpoint URL and a{" "}
                    <code>Authorization: Bearer</code> key.
                  </p>
                )}

                {/* [S-475] claude.ai / Claude Desktop use OAuth, not a pasted
                    key — they only need the endpoint URL (copyable above). */}
                <div className="set__mcp__claudeai">
                  <div className="set__mcp__lbl">Add to claude.ai (no key)</div>
                  <p className="set__mcp__note">
                    On claude.ai or Claude Desktop, choose{" "}
                    <strong>Add custom connector</strong> and paste only the
                    endpoint URL above — no API key. Claude signs you in through
                    ContractStudio (OAuth) and is scoped to your workspace. The
                    key-based command above is for Claude Code and other
                    programmatic clients.
                  </p>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
}

// [S-404] Deploy / undeploy the organization's dedicated, isolated MCP server.
// Honest when the feature isn't provisioned in this environment yet.
function McpDeploymentBlock({
  available,
  loading,
  deployment,
  onDeploy,
  onUndeploy,
  deploying,
  undeploying,
  actionError,
}: {
  available: boolean;
  loading: boolean;
  deployment: import("../data/mcpDeployQuery").McpDeployment | null;
  onDeploy: () => void;
  onUndeploy: () => void;
  deploying: boolean;
  undeploying: boolean;
  actionError: Error | null;
}): JSX.Element {
  if (loading) {
    return (
      <div className="set__mcp__deploy" data-testid="set-mcp-deploy">
        <div className="set__mcp__lbl">Dedicated MCP server</div>
        <p className="set__mcp__note"><Spinner size={13} /> Loading deployment status…</p>
      </div>
    );
  }
  if (!available) {
    return (
      <div className="set__mcp__deploy" data-testid="set-mcp-deploy">
        <div className="set__mcp__lbl">Dedicated MCP server</div>
        <p className="set__mcp__note">
          Per-tenant deployment isn't enabled in this environment yet. Once your
          ContractStudio operator completes the one-time provisioning, you'll be
          able to deploy your organization's own isolated MCP server here.
        </p>
      </div>
    );
  }
  const status = deployment?.status ?? "undeployed";
  const statusText: Record<typeof status, string> = {
    undeployed: "Not deployed",
    deploying: "Deploying…",
    deployed: "Deployed",
    error: "Error",
  };
  return (
    <div className="set__mcp__deploy" data-testid="set-mcp-deploy">
      <div className="set__mcp__lbl">Dedicated MCP server</div>
      <p className="set__mcp__note">
        Deploy a server isolated to your organization. It scales to zero (no idle
        cost); <strong>Undeploy</strong> removes it entirely and stops any
        charges.
      </p>
      <div className="set__mcp__deployrow">
        <span
          className={`set__mcp__status is-${status === "deployed" ? "online" : status === "error" ? "offline" : "unknown"}`}
          data-testid="set-mcp-deploy-status"
        >
          <span className="dot" /> {statusText[status]}
        </span>
        {status === "deployed" ? (
          <Button
            variant="secondary"
            onClick={onUndeploy}
            disabled={undeploying}
            data-testid="set-mcp-undeploy"
          >
            {undeploying ? "Undeploying…" : "Undeploy"}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={onDeploy}
            disabled={deploying}
            data-testid="set-mcp-deploy-btn"
          >
            {deploying ? "Deploying…" : "Deploy connector"}
          </Button>
        )}
      </div>
      {status === "error" && deployment?.lastError !== null && deployment !== null && (
        <Alert label="Deploy failed" tone="error">
          {deployment.lastError}
        </Alert>
      )}
      {actionError !== null && (
        <Alert label="Action failed" tone="error">
          {actionError.message}
        </Alert>
      )}
    </div>
  );
}

function ApiKeyRow({
  record,
  onRevoke,
  revoking,
}: {
  record: ApiKeyRecord;
  onRevoke: () => void;
  revoking: boolean;
}): JSX.Element {
  const isRevoked = record.revokedAt !== null;
  return (
    <li
      className={`set__apik__row${isRevoked ? " is-revoked" : ""}`}
      data-testid={`set-apikeys-row-${record.id}`}
    >
      <span className="set__apik__row__name">
        {record.name}
        {isRevoked && (
          <em>revoked · {formatShortDate(record.revokedAt!)}</em>
        )}
      </span>
      <span className="set__apik__row__tier">{record.accessTier}</span>
      <span className="set__apik__row__meta">
        by {record.createdBy} · {formatShortDate(record.createdAt)}
      </span>
      <span className="set__apik__row__act">
        {!isRevoked && (
          <Button
            variant="ghost"
            className="btn--xs"
            onClick={onRevoke}
            disabled={revoking}
            data-testid={`set-apikeys-revoke-${record.id}`}
          >
            {revoking ? "Revoking…" : "Revoke"}
          </Button>
        )}
      </span>
    </li>
  );
}


// ────────────────────────────────────────────────────────────────────────
// [S-242] Notifications — recipients (email list) + webhook URL +
// events. Saves a single PUT to /preferences/notifications.
// ────────────────────────────────────────────────────────────────────────

const NOTIFICATION_EVENT_LABELS: Record<NotificationEvent, string> = {
  template_certified: "Template certified",
  template_deprecated: "Template deprecated",
  template_under_review: "Template flagged for review",
  ingestion_failed: "Ingestion failed",
  attestation_due: "Re-attestation due",
};

function NotificationsSection({ tenantId }: { tenantId: string }): JSX.Element {
  const prefsQuery = useTenantPreferences(tenantId);
  const update = useUpdateNotifications();
  const stored = prefsQuery.data?.notifications ?? null;

  const [recipientsText, setRecipientsText] = useState<string>("");
  const [webhookUrl, setWebhookUrl] = useState<string>("");
  const [events, setEvents] = useState<NotificationEvent[]>([]);

  useEffect(() => {
    if (stored !== null) {
      setRecipientsText(stored.recipients.join(", "));
      setWebhookUrl(stored.webhookUrl ?? "");
      setEvents(stored.events);
    }
  }, [stored?.recipients.join(","), stored?.webhookUrl, stored?.events.join(",")]);

  const parsedRecipients = recipientsText
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const recipientsInvalid = parsedRecipients.some(
    (e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
  );
  const webhookInvalid =
    webhookUrl.length > 0 && !/^https?:\/\//i.test(webhookUrl);

  const canSave =
    tenantId.length > 0 &&
    !recipientsInvalid &&
    !webhookInvalid &&
    !update.isPending;

  const handleToggleEvent = (ev: NotificationEvent): void => {
    setEvents((prev) =>
      prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]
    );
  };

  const handleSave = (): void => {
    if (!canSave) return;
    update.mutate({
      tenantId,
      notifications: {
        recipients: parsedRecipients,
        webhookUrl: webhookUrl.trim().length > 0 ? webhookUrl.trim() : null,
        events,
      },
    });
  };

  return (
    <section className="set__sec" data-testid="set-notifications">
      <SectionHead
        id="notifications"
        title="Notifications"
        sub="Where to send alerts when events fire — emails go to every recipient; the webhook receives a POST per event."
        action={
          tenantId.length === 0 ? (
            <Button variant="primary" className="btn--s" disabled>
              Save
            </Button>
          ) : (
            <Button
              variant="primary"
              className="btn--s"
              onClick={handleSave}
              disabled={!canSave}
              data-testid="set-notifications-save"
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          )
        }
      />
      <div className="set__row">
        <div className="set__row__k">
          Email recipients
          <em>Comma-separated. Each address gets every selected event.</em>
        </div>
        <div className="set__row__v">
          <textarea
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            placeholder="schen@studio.com, ops@studio.com"
            rows={2}
            className="set__notif__recip"
            data-testid="set-notifications-recipients"
          />
          {recipientsInvalid && (
            <div className="set__notif__err">
              One or more entries don't look like email addresses.
            </div>
          )}
        </div>
      </div>
      <div className="set__row">
        <div className="set__row__k">
          Webhook URL
          <em>Optional. ContractStudio POSTs a JSON payload per event.</em>
        </div>
        <div className="set__row__v">
          <input
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://hooks.studio.com/csai-events"
            className="set__notif__webhook"
            data-testid="set-notifications-webhook"
          />
          {webhookInvalid && (
            <div className="set__notif__err">
              Webhook URL must start with http:// or https://
            </div>
          )}
        </div>
      </div>
      <div className="set__row">
        <div className="set__row__k">
          Events
          <em>Subscribe to the events you want to be alerted on.</em>
        </div>
        <div className="set__row__v">
          <div className="set__notif__events">
            {NOTIFICATION_EVENTS.map((ev) => (
              <Checkbox
                key={ev}
                checked={events.includes(ev)}
                onCheckedChange={() => handleToggleEvent(ev)}
                label={NOTIFICATION_EVENT_LABELS[ev]}
              />
            ))}
          </div>
        </div>
      </div>
      {update.isError && (
        <div className="set__sec__err" role="alert">
          Save failed — {update.error?.message ?? "unknown error"}
        </div>
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// [S-243] Data & retention section — audit-log CSV export with optional
// date-range filter. The download goes through useExportAuditLog which
// streams the CSV from /tenants/me/audit/export.
// ────────────────────────────────────────────────────────────────────────

function DataSection({ tenantId }: { tenantId: string }): JSX.Element {
  const exportAudit = useExportAuditLog();
  const today = new Date().toISOString().slice(0, 10);
  const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const [fromDate, setFromDate] = useState<string>(ninetyDaysAgo);
  const [toDate, setToDate] = useState<string>(today);

  const handleExport = (): void => {
    if (tenantId.length === 0 || exportAudit.isPending) return;
    exportAudit.mutate({
      fromIso: `${fromDate}T00:00:00.000Z`,
      toIso: `${toDate}T23:59:59.999Z`,
    });
  };

  return (
    <section className="set__sec" data-testid="set-data">
      <SectionHead
        id="data"
        title="Data & retention"
        sub="Export the per-tenant audit trail. Retention windows live in the Security section above."
        action={
          tenantId.length === 0 ? (
            <Button variant="primary" className="btn--s" disabled>
              Export CSV
            </Button>
          ) : (
            <Button
              variant="primary"
              className="btn--s"
              onClick={handleExport}
              disabled={exportAudit.isPending}
              data-testid="set-data-export"
            >
              {exportAudit.isPending ? "Exporting…" : "Export CSV"}
            </Button>
          )
        }
      />
      <div className="set__row">
        <div className="set__row__k">
          Audit log export
          <em>One CSV row per audit event; capped at 50,000 rows.</em>
        </div>
        <div className="set__row__v">
          <div className="set__data__dates">
            <label className="set__data__date">
              <span>From</span>
              <input
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="set-data-from"
              />
            </label>
            <label className="set__data__date">
              <span>To</span>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                max={today}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="set-data-to"
              />
            </label>
          </div>
          {exportAudit.isError && (
            <div className="set__sec__err" role="alert">
              Export failed — {exportAudit.error?.message ?? "unknown error"}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
